// src/features/secureDocuments/components/SecureDocumentModal.tsx
import React, { useState, useRef, useEffect } from 'react';
import { X, Upload, File, Trash2, AlertCircle, Lock, Users, Download, Eye, History, Clock } from 'lucide-react';
import { useAuth } from '../../auth/hooks/useAuth';
import { secureDocumentService, SecureAccessLogEntry } from '../services/SecureDocumentService';
import { supabase } from '../../../shared/services/supabase';
import { netlifyFetch } from '../../../shared/services/netlifyFetch';
import { customerContactService } from '../../../services/customer/CustomerContactService';
import { SenderSelection } from '../../deadlines/components/SenderSelection';
import { useSenderManagement } from '../../deadlines/hooks/useSenderManagement';
import { berlinNowLocalString, berlinTomorrowLocalString, formatBerlinDateTime } from '../../../shared/utils/berlinTime';
import { CustomerContact } from '../../../shared/types';
import { 
  SecureDocumentModalProps, 
  PendingUploadFile,
  SecureUploadWithFiles,
  FolgepaketAenderung,
  ALLOWED_MIME_TYPES,
  MAX_FILE_SIZE,
  MAX_TOTAL_SIZE,
  LARGE_FILE_WARN_SIZE
} from '../types';
import {
  pruefeLohnlauf,
  istLohnlaufDatei,
  isoZuDeutsch,
  PruefErgebnis
} from '../validation/lohnlaufPruefung';
import { setzeAusfuehrungstermin } from '../validation/sepaSofortAusfuehrung';
import { LohnlaufPruefungPanel } from './LohnlaufPruefungPanel';

type ModalTab = 'senden' | 'downloadbereich';

// NEU (Überschrift): Kategorien für die kundensichtbare Paket-Überschrift.
// Die vier Standardfälle setzen einen strukturierten Titel mit Bezugsmonat;
// „Sonstiges" erlaubt einen freien Titel ohne Monat.
type TitelKategorie = '' | 'probe_1' | 'probe_2' | 'abrechnung_1' | 'abrechnung_2' | 'sonstiges';

// Wording an den Fristenkalender angelehnt (Erster/Zweiter Lohnlauf).
const TITEL_KATEGORIE_LABELS: Record<string, string> = {
  probe_1: 'Probeabrechnung Erster Lohnlauf',
  probe_2: 'Probeabrechnung Zweiter Lohnlauf',
  abrechnung_1: 'Abrechnung Erster Lohnlauf',
  abrechnung_2: 'Abrechnung Zweiter Lohnlauf'
};

// Punkt 2 (Veralten-Tracking): Mapping der Überschrift-Kategorie auf den
// paket_typ-Gruppierungsschlüssel. "Sonstiges" bewusst NICHT gemappt -> das
// Paket nimmt nicht am Veralten teil (kein natürlicher Vorgänger).
const MANUELL_PAKET_TYP: Record<string, string | undefined> = {
  abrechnung_1: 'manuell_abrechnung_1',
  abrechnung_2: 'manuell_abrechnung_2',
  probe_1: 'manuell_probe_1',
  probe_2: 'manuell_probe_2'
};

// Vorbelegung beim terminierten Versand: Folgetag 06:30 Uhr (Berliner Zeit).
// Greift nur, solange noch kein Zeitpunkt gewählt ist.
const STANDARD_VERSAND_STUNDE = 6;
const STANDARD_VERSAND_MINUTE = 30;

// Überschriften, bei denen ein Ansprechpartner ohne Vollzugriff eine Warnung
// auslöst. Der Versand bleibt möglich, muss aber je Vorgang bestätigt werden.
const VOLLZUGRIFF_WARN_KATEGORIEN: TitelKategorie[] = ['abrechnung_1', 'probe_1'];

const MONATS_NAMEN = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

// Muster für den Bezugsmonat aus DATEV-Dateinamen: „_MM_JJJJ_"
// (z. B. 58904_06_2026_Brutto_Netto_Probe_...). Monat 01–12, Jahr 20xx.
const MONATS_MUSTER = /_(0[1-9]|1[0-2])_(20\d{2})_/;

// Auszahlungsliste (OPPM): Zu jedem Paket mit einer SEPA-Überweisungsdatei für
// Lohn und Gehalt gehört eine CSV mit Personalnummer, Name und
// Auszahlungsbetrag. Erzeugt wird sie serverseitig von der Edge Function
// sepa-gehaltszahlungen, noch bevor das Paket angelegt wird. Geht der Abgleich
// zwischen SEPA-Datei und DATEV-Abstimmliste nicht auf, sperrt der Upload;
// die Sperre lässt sich wie bei der Lohnlaufprüfung übergehen, das Paket
// entsteht dann ohne Auszahlungsliste.
//
// Verwendungszweck-Ersetzung (OPPM): Ist für den Kunden eine Regel hinterlegt,
// etwa für einen Freigänger, dessen Lohn nur mit dem Verwendungszweck aus dem
// JVA-Schreiben zugeordnet werden kann, liefert dieselbe Prüfung die
// angepasste SEPA-Datei mit. Hochgeladen wird sie anstelle des Originals, aber
// nur zusammen mit einer erzeugten Auszahlungsliste. Die Dateiauswahl selbst
// bleibt unverändert, damit beide Prüfungen weiter auf den DATEV-Originalen
// laufen.
//
// Sofortige Ausführung: Ist die SEPA-Datei für Lohn und Gehalt zur sofortigen
// Ausführung ausgewählt, geht schon die auf den Versandtag umgestellte Fassung
// in diese Prüfung. Auszahlungsliste und angepasste Datei tragen dann bereits
// den Versandtag.
const OPPM_KUNDENGRUPPE = 'd4816049-2926-4826-9296-960340077145';

const istSepaLohnGehaltDatei = (dateiname: string): boolean => {
  const n = dateiname.toLowerCase();
  return n.startsWith('sepa-') && n.includes('lohn_gehalt') && n.endsWith('.xml');
};

const istAuswertungsPdfDatei = (dateiname: string): boolean => {
  const n = dateiname.toLowerCase();
  return n.includes('div_auswertungen') && n.endsWith('.pdf');
};

interface AuszahlungslistePruefung {
  gesperrt: boolean;
  erfolgreich: boolean;
  probleme: string[];
  dateiname: string | null;
  zeilenAnzahl: number;
  summeText: string | null;
  sepaDateiname: string;
}

// Ergebnis der Verwendungszweck-Ersetzung aus derselben Prüfung. Angezeigt
// werden nur "ersetzt" (grün) und "gesperrt" (rot). "nicht_ausgefuehrt" bleibt
// unsichtbar und dient nur dem Protokoll, falls die beanstandete
// Auszahlungsliste übergangen wird.
interface VerwendungszweckErsetzung {
  status: 'nicht_ausgefuehrt' | 'ersetzt' | 'gesperrt';
  gesperrt: boolean;
  probleme: string[];
  personalnummern: string[];
  ersetzungen: Array<{ personalnummer: string; position: number; betragText: string; neuerText: string }>;
  sepaDateiname: string;
}

const dateiZuBase64 = (datei: Blob): Promise<string> =>
  new Promise((aufloesen, ablehnen) => {
    const leser = new FileReader();
    leser.onload = () => {
      const ergebnis = String(leser.result);
      aufloesen(ergebnis.slice(ergebnis.indexOf(',') + 1));
    };
    leser.onerror = () => ablehnen(new Error('Datei konnte nicht gelesen werden'));
    leser.readAsDataURL(datei);
  });

const base64ZuBytes = (base64: string): Uint8Array => {
  const roh = atob(base64);
  const bytes = new Uint8Array(roh.length);
  for (let i = 0; i < roh.length; i++) bytes[i] = roh.charCodeAt(i);
  return bytes;
};

export const SecureDocumentModal: React.FC<SecureDocumentModalProps> = ({
  isOpen,
  customer,
  onClose,
  onSuccess
}) => {
  // Tab-State
  const [activeTab, setActiveTab] = useState<ModalTab>('senden');

  // === SENDEN-TAB States ===
  const [pendingFiles, setPendingFiles] = useState<PendingUploadFile[]>([]);
  const [kommentar, setKommentar] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // NEU (Überschrift): Pflicht-Überschrift des Pakets. Kategorie steuert den
  // kundensichtbaren Titel; bei „Sonstiges" wird der Freitext verwendet. Der
  // manuelle Monat/Jahr-Fallback greift nur bei den Standardfällen, wenn aus
  // keinem Dateinamen ein Bezugsmonat ableitbar ist (Vormonat vorbelegt).
  const [titelKategorie, setTitelKategorie] = useState<TitelKategorie>('');
  const [titelSonstiges, setTitelSonstiges] = useState('');
  const [manualMonth, setManualMonth] = useState<number>(new Date().getMonth() === 0 ? 12 : new Date().getMonth());
  const [manualYear, setManualYear] = useState<number>(new Date().getMonth() === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear());

  // Stufe 2: terminierter Versand — Sofort/Geplant plus Berliner Wanduhrzeit
  // fürs datetime-local-Feld. Bei „Geplant" wird das Paket ohne Mail angelegt;
  // der Minuten-Cron (secure-scheduled-send) versendet zum Zeitpunkt.
  const [scheduleMode, setScheduleMode] = useState<'now' | 'later'>('now');
  const [scheduledLocal, setScheduledLocal] = useState('');
  
  // NEU: Kontakt-States
  const [contacts, setContacts] = useState<CustomerContact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  
  // === DOWNLOADBEREICH-TAB States ===
  const [previewUploads, setPreviewUploads] = useState<SecureUploadWithFiles[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set());
  const [confirmDeleteUploadId, setConfirmDeleteUploadId] = useState<string | null>(null);
  const [deletingUploadIds, setDeletingUploadIds] = useState<Set<string>>(new Set());

  // Stufe 2: geplante und fehlgeschlagene Pakete des gewählten Ansprechpartners
  // (Mitarbeiter-Sicht, Abschnitt „Geplante Sendungen"). Der Kunde sieht diese
  // Pakete erst nach erfolgreichem Versand.
  const [scheduledUploads, setScheduledUploads] = useState<SecureUploadWithFiles[]>([]);
  const [scheduledLoading, setScheduledLoading] = useState(false);
  const [cancellingUploadIds, setCancellingUploadIds] = useState<Set<string>>(new Set());
  const [confirmCancelUploadId, setConfirmCancelUploadId] = useState<string | null>(null);

  // Punkt 2 (Veralten-Tracking): aufgeklappte veraltete Pakete in der
  // Kunden-Vorschau (Downloadbereich-Tab). Standardmäßig eingeklappt.
  const [expandierteVeralteteVorschau, setExpandierteVeralteteVorschau] = useState<Set<string>>(new Set());

  // NEU (Zugriffsprotokoll): Login-/Download-Historie des gewählten
  // Ansprechpartners. Wird geladen, sobald der Downloadbereich-Tab
  // mit ausgewähltem Kontakt aktiv ist.
  const [accessLog, setAccessLog] = useState<SecureAccessLogEntry[]>([]);
  const [accessLogLoading, setAccessLogLoading] = useState(false);

  // Stufe 3.1: DATEV-Dokumententyp-Beschreibungen und Abschnitte für xtax_portal-Files.
  // Bulk-Lookup auf secure_customer_upload_files → datev_document_explanations
  // (+ datev_document_sections) wird ausgelöst sobald previewUploads sich ändern.
  // Map: file.id → {titel, beschreibung, section_key, section_titel}.
  // Bei manuellen Uploads (datev_document_type IS NULL) fehlt der File-Eintrag in
  // der Map und im Render wird nichts zusätzlich angezeigt / nicht gruppiert.
  const [explanationByFileId, setExplanationByFileId] = useState<Record<string, { titel: string; beschreibung: string; section_key: string | null; section_titel: string | null }>>({});
  
  // Prüfung der Lohnlaufunterlagen (DATEV): Ergebnis, Laufzustand und die aus
  // customers nachgeladenen Nummern. Die Nummern stehen nicht im customer-Prop,
  // deshalb derselbe direkte Supabase-Lookup wie bei den Dokumententyp-
  // Beschreibungen weiter unten.
  const [lohnlaufPruefung, setLohnlaufPruefung] = useState<PruefErgebnis | null>(null);
  const [lohnlaufPruefungLaeuft, setLohnlaufPruefungLaeuft] = useState(false);
  const [kundenNummern, setKundenNummern] = useState<{ beraternummer: string | null; mandantennummer: string | null; customer_group_id: string | null } | null>(null);

  // Übergehen der Upload-Sperre aus der Prüfung. Gilt ausschließlich für den
  // gerade geöffneten Dialog und wird beim Öffnen des Modals zurückgesetzt.
  // Die Prüfung läuft unverändert weiter und alle Befunde bleiben sichtbar;
  // es entfällt lediglich die Blockade des Upload-Buttons.
  const [pruefungUebergehen, setPruefungUebergehen] = useState(false);

  // Sofortige Ausführung von SEPA-Dateien: Dateinamen (genau wie in der
  // Auswahl) der SEPA-Dateien, deren Ausführungstermin beim Upload auf den
  // Versandtag umgestellt wird. Gilt nur für den gerade geöffneten Dialog und
  // wird beim Öffnen des Modals zurückgesetzt.
  const [sofortDateien, setSofortDateien] = useState<string[]>([]);

  // Auszahlungsliste (OPPM): Ergebnis der serverseitigen Prüfung, die erzeugte
  // CSV als fertige Datei und das Übergehen der Sperre. Alle drei gelten nur
  // für die aktuelle Dateiauswahl und werden bei jeder Änderung neu bestimmt.
  const [auszahlungsliste, setAuszahlungsliste] = useState<AuszahlungslistePruefung | null>(null);
  const [auszahlungslisteLaeuft, setAuszahlungslisteLaeuft] = useState(false);
  const [auszahlungslisteUebergehen, setAuszahlungslisteUebergehen] = useState(false);
  const [auszahlungslisteDatei, setAuszahlungslisteDatei] = useState<File | null>(null);

  // Verwendungszweck-Ersetzung (OPPM): Ergebnis, Übergehen der Sperre und die
  // angepasste SEPA-Datei samt dem Original, an dessen Stelle sie tritt.
  const [ersetzung, setErsetzung] = useState<VerwendungszweckErsetzung | null>(null);
  const [ersetzungUebergehen, setErsetzungUebergehen] = useState(false);
  const [ersetzteSepaDatei, setErsetzteSepaDatei] = useState<{ original: File; ersatz: File } | null>(null);

  // Bestätigung „Trotzdem senden" für Ansprechpartner ohne Vollzugriff bei
  // Unterlagen des Ersten Lohnlaufs. Gilt nur für den aktuellen Vorgang und
  // wird beim Öffnen sowie bei jedem Wechsel von Ansprechpartner oder
  // Überschrift zurückgesetzt.
  const [vollzugriffWarnungUebergehen, setVollzugriffWarnungUebergehen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();

  // Stufe 2 — Absender-Auswahl „Senden als andere Person": Foto-Pflicht bleibt
  // aktiv (requirePhoto-Default true), da die Paket-Mail die Foto-Signatur der
  // Finalisieren-Pipelines nutzt. Das Laden ist auf isOpen UND eine aufgelöste
  // User-E-Mail gegated, damit das Smart-Matching des Hooks den eingeloggten
  // Nutzer sicher als Default trifft. Der gewählte Absender bestimmt Titel,
  // Signatur und Duz-Gate der Mail; erstellt_von_* bleibt die echte Person.
  const {
    senders,
    selectedSender,
    setSelectedSender,
    loading: sendersLoading,
    error: sendersError,
    photoValidationErrors
  } = useSenderManagement(isOpen && Boolean(user?.email), user?.email);

  // Versandtag (Berliner Datum, YYYY-MM-DD) für die sofortige Ausführung von
  // SEPA-Dateien: beim Sofortversand heute, beim geplanten Versand der Tag des
  // gewählten Zeitpunkts. So liegt der Ausführungstermin an dem Tag, an dem
  // der Kunde die Datei erhält. sofortTermin ist nur gesetzt, solange
  // mindestens eine Datei zur sofortigen Ausführung ausgewählt ist; sonst
  // lösen Änderungen am Versandzeitpunkt keine neue Prüfung aus.
  const versandtag = scheduleMode === 'later' && scheduledLocal
    ? scheduledLocal.slice(0, 10)
    : berlinNowLocalString().slice(0, 10);
  const sofortTermin = sofortDateien.length > 0 ? versandtag : null;

  // NEU: Kontakte laden beim Öffnen
  useEffect(() => {
    if (!isOpen) return;
    
    const loadContacts = async () => {
      setContactsLoading(true);
      setSelectedContactId(null);
      setError(null);
      setPreviewError(null);
      setActiveTab('senden');
      setAccessLog([]);
      // Stufe 2: Versandplanung und Geplant-Liste beim Öffnen zurücksetzen
      setScheduleMode('now');
      setScheduledLocal('');
      setScheduledUploads([]);
      setConfirmCancelUploadId(null);
      // Punkt 2 (Veralten-Tracking): aufgeklappte Vorschau-Pakete zurücksetzen
      setExpandierteVeralteteVorschau(new Set());
      // Übergehen der Prüf-Sperre gilt nur für einen Upload: bei jedem Öffnen aus.
      setPruefungUebergehen(false);
      // Sofortige Ausführung gilt nur für einen Upload: bei jedem Öffnen aus.
      setSofortDateien([]);
      // Auszahlungsliste ebenfalls zurücksetzen
      setAuszahlungsliste(null);
      setAuszahlungslisteLaeuft(false);
      setAuszahlungslisteUebergehen(false);
      setAuszahlungslisteDatei(null);
      setErsetzung(null);
      setErsetzungUebergehen(false);
      setErsetzteSepaDatei(null);
      // Bestätigung der Vollzugriff-Warnung gilt nur für einen Vorgang
      setVollzugriffWarnungUebergehen(false);
      // NEU (Überschrift): Felder zurücksetzen; manuellen Fallback-Monat auf
      // den Vormonat vorbelegen (bei Januar → Dezember des Vorjahres).
      setTitelKategorie('');
      setTitelSonstiges('');
      const jetzt = new Date();
      const vormonat = jetzt.getMonth() === 0 ? 12 : jetzt.getMonth();
      const vormonatJahr = jetzt.getMonth() === 0 ? jetzt.getFullYear() - 1 : jetzt.getFullYear();
      setManualMonth(vormonat);
      setManualYear(vormonatJahr);
      
      const loadedContacts = await customerContactService.getContactsForCustomer(customer.id);
      setContacts(loadedContacts);
      
      // Auto-Select: Bei genau einem Kontakt automatisch wählen
      if (loadedContacts.length === 1) {
        setSelectedContactId(loadedContacts[0].id);
      }
      
      setContactsLoading(false);
    };
    
    loadContacts();
  }, [isOpen, customer.id]);

  // Downloadbereich laden wenn Tab gewechselt wird und Kontakt ausgewählt ist
  useEffect(() => {
    if (activeTab === 'downloadbereich' && selectedContactId) {
      loadPreviewUploads(selectedContactId);
      loadAccessLog(selectedContactId);
      loadScheduledUploads(selectedContactId);
    }
  }, [activeTab, selectedContactId]);

  // Stufe 3.1: DATEV-Dokumententyp-Beschreibungen anreichern.
  // Anders als in der Mandanten-Sicht (wo die Netlify-Function das übernimmt)
  // läuft die Mitarbeiter-Sicht direkt gegen Supabase. Wir holen daher die
  // datev_document_type-Werte separat (secureDocumentService liefert sie nicht)
  // und mappen sie über datev_document_explanations.
  useEffect(() => {
    if (previewUploads.length === 0) {
      setExplanationByFileId({});
      return;
    }

    const allFileIds = previewUploads.flatMap(u => u.files.map(f => f.id));
    if (allFileIds.length === 0) {
      setExplanationByFileId({});
      return;
    }

    let cancelled = false;
    const loadExplanations = async () => {
      try {
        // 1) Doc-Types pro File laden
        const { data: filesWithType, error: filesErr } = await supabase
          .from('secure_customer_upload_files')
          .select('id, datev_document_type')
          .in('id', allFileIds);

        if (cancelled) return;
        if (filesErr) {
          console.warn('[Preview] datev_document_type-Lookup fehlgeschlagen (non-blocking):', filesErr.message);
          return;
        }
        if (!filesWithType) return;

        // 2) Distinct Doc-Types ermitteln
        const docTypes = new Set<string>();
        for (const f of filesWithType) {
          if (f.datev_document_type) docTypes.add(f.datev_document_type);
        }
        if (docTypes.size === 0) {
          if (!cancelled) setExplanationByFileId({});
          return;
        }

        // 3) Bulk-Lookup auf Explanations (inkl. Abschnitts-Key)
        const { data: explanations, error: explErr } = await supabase
          .from('datev_document_explanations')
          .select('document_type, titel, beschreibung, section_key')
          .in('document_type', Array.from(docTypes));

        if (cancelled) return;
        if (explErr) {
          console.warn('[Preview] datev_document_explanations-Lookup fehlgeschlagen (non-blocking):', explErr.message);
          return;
        }
        if (!explanations) return;

        // 3b) Abschnitts-Titel laden (kleine Lookup-Tabelle, alle Abschnitte).
        // Ebenfalls non-blocking: fehlt die Zuordnung, bleibt der Abschnitts-Titel
        // null und die Vorschau rendert flach.
        const { data: sections, error: sectionsErr } = await supabase
          .from('datev_document_sections')
          .select('section_key, titel');

        if (cancelled) return;
        if (sectionsErr) {
          console.warn('[Preview] datev_document_sections-Lookup fehlgeschlagen (non-blocking):', sectionsErr.message);
        }
        const sectionTitelByKey: Record<string, string> = {};
        if (sections) {
          for (const s of sections) {
            sectionTitelByKey[s.section_key] = s.titel;
          }
        }

        // 4) Mapping doc_type → {titel, beschreibung, section_key}
        const byDocType: Record<string, { titel: string; beschreibung: string; section_key: string | null }> = {};
        for (const e of explanations) {
          byDocType[e.document_type] = { titel: e.titel, beschreibung: e.beschreibung, section_key: e.section_key ?? null };
        }

        // 5) Mapping file.id → {titel, beschreibung, section_key, section_titel}
        const byFileId: Record<string, { titel: string; beschreibung: string; section_key: string | null; section_titel: string | null }> = {};
        for (const f of filesWithType) {
          if (f.datev_document_type && byDocType[f.datev_document_type]) {
            const info = byDocType[f.datev_document_type];
            byFileId[f.id] = {
              titel: info.titel,
              beschreibung: info.beschreibung,
              section_key: info.section_key,
              section_titel: info.section_key ? (sectionTitelByKey[info.section_key] ?? null) : null
            };
          }
        }

        if (!cancelled) setExplanationByFileId(byFileId);
      } catch (err) {
        if (!cancelled) {
          console.warn('[Preview] Beschreibungs-Lookup fehlgeschlagen (non-blocking):', err);
        }
      }
    };

    loadExplanations();

    return () => {
      cancelled = true;
    };
  }, [previewUploads]);

  // Berater- und Mandantennummer des Kunden laden, dazu die Kundengruppe für
  // die Auszahlungsliste. Sie sind nicht Teil von SecureDocumentModalProps;
  // der Lookup läuft deshalb direkt gegen customers. Fehler sind non-blocking.
  // Ohne Nummern meldet die Prüfung die Zuordnung als ungeprüft, statt den
  // Upload zu verhindern.
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    const ladeKundenNummern = async () => {
      try {
        const { data, error: nummernFehler } = await supabase
          .from('customers')
          .select('beraternummer, mandantennummer, customer_group_id')
          .eq('id', customer.id)
          .maybeSingle();

        if (cancelled) return;
        if (nummernFehler) {
          console.warn('[Pruefung] Kundennummern-Lookup fehlgeschlagen (non-blocking):', nummernFehler.message);
          setKundenNummern({ beraternummer: null, mandantennummer: null, customer_group_id: null });
          return;
        }
        setKundenNummern({
          beraternummer: data?.beraternummer ?? null,
          mandantennummer: data?.mandantennummer ?? null,
          customer_group_id: data?.customer_group_id ?? null
        });
      } catch (err) {
        if (!cancelled) {
          console.warn('[Pruefung] Kundennummern-Lookup fehlgeschlagen (non-blocking):', err);
          setKundenNummern({ beraternummer: null, mandantennummer: null, customer_group_id: null });
        }
      }
    };

    setKundenNummern(null);
    ladeKundenNummern();

    return () => {
      cancelled = true;
    };
  }, [isOpen, customer.id]);

  // Entfernte Dateien verlieren ihre Auswahl für die sofortige Ausführung.
  // Bleibt die Auswahl gleich, wird derselbe Stand zurückgegeben, damit keine
  // unnötige neue Prüfung ausgelöst wird.
  useEffect(() => {
    const namen = pendingFiles.filter(f => f.isValid).map(f => f.file.name);
    setSofortDateien(vorher => {
      const bleibend = vorher.filter(name => namen.includes(name));
      return bleibend.length === vorher.length ? vorher : bleibend;
    });
  }, [pendingFiles]);

  // Prüfung anstoßen, sobald sich die Dateiauswahl ändert. Sie greift nur,
  // wenn mindestens eine Datei dem DATEV-Muster folgt; alle anderen Uploads
  // laufen unverändert weiter. Auch eine geänderte Auswahl zur sofortigen
  // Ausführung stößt die Prüfung neu an, weil sie die Befunde verändert.
  useEffect(() => {
    const zuPruefen = pendingFiles.filter(f => f.isValid).map(f => f.file);

    if (!zuPruefen.some(f => istLohnlaufDatei(f.name))) {
      setLohnlaufPruefung(null);
      setLohnlaufPruefungLaeuft(false);
      return;
    }

    // Auf die Kundennummern warten, sonst würde die Zuordnung fälschlich als
    // ungeprüft gemeldet, obwohl der Lookup nur noch nicht zurück ist.
    if (kundenNummern === null) return;

    let cancelled = false;
    setLohnlaufPruefungLaeuft(true);

    const fuehrePruefungAus = async () => {
      try {
        const sofort = sofortTermin
          ? { dateinamen: sofortDateien, termin: sofortTermin }
          : null;
        const ergebnis = await pruefeLohnlauf(zuPruefen, kundenNummern, new Date(), sofort);
        if (!cancelled) setLohnlaufPruefung(ergebnis);
      } catch (err) {
        console.warn('[Pruefung] Prüfung fehlgeschlagen (non-blocking):', err);
        if (!cancelled) setLohnlaufPruefung(null);
      } finally {
        if (!cancelled) setLohnlaufPruefungLaeuft(false);
      }
    };

    fuehrePruefungAus();

    return () => {
      cancelled = true;
    };
  }, [pendingFiles, kundenNummern, sofortDateien, sofortTermin]);

  // Auszahlungsliste erzeugen und prüfen. Greift nur bei der OPPM-Kundengruppe
  // und nur, wenn in der Auswahl eine SEPA-Überweisungsdatei für Lohn und
  // Gehalt liegt. Alles Weitere entscheidet die Edge Function; das Frontend
  // hält kein eigenes Regelwerk vor. Jede Änderung der Dateiauswahl setzt ein
  // zuvor gesetztes Übergehen zurück.
  useEffect(() => {
    if (!isOpen) return;

    const gueltige = pendingFiles.filter(f => f.isValid).map(f => f.file);
    const sepaDatei = gueltige.find(f => istSepaLohnGehaltDatei(f.name));

    if (kundenNummern === null) return;

    if (kundenNummern.customer_group_id !== OPPM_KUNDENGRUPPE || !sepaDatei) {
      setAuszahlungsliste(null);
      setAuszahlungslisteLaeuft(false);
      setAuszahlungslisteUebergehen(false);
      setAuszahlungslisteDatei(null);
      setErsetzung(null);
      setErsetzungUebergehen(false);
      setErsetzteSepaDatei(null);
      return;
    }

    let cancelled = false;
    setAuszahlungslisteLaeuft(true);
    setAuszahlungsliste(null);
    setAuszahlungslisteDatei(null);
    setAuszahlungslisteUebergehen(false);
    setErsetzung(null);
    setErsetzungUebergehen(false);
    setErsetzteSepaDatei(null);

    const fuehreAus = async () => {
      try {
        // Sofortige Ausführung: Ist die Datei dafür ausgewählt, geht schon die
        // auf den Versandtag umgestellte Fassung in die Prüfung. Scheitert die
        // Umstellung, sperrt das wie jede andere Beanstandung.
        let pruefDatei: File = sepaDatei;
        if (sofortTermin && sofortDateien.includes(sepaDatei.name)) {
          const umgestellt = await setzeAusfuehrungstermin(sepaDatei, sofortTermin);
          if (cancelled) return;
          if (!umgestellt.ok) {
            setAuszahlungsliste({
              gesperrt: true,
              erfolgreich: false,
              probleme: [`Der Ausführungstermin konnte nicht umgestellt werden: ${umgestellt.fehler}`],
              dateiname: null,
              zeilenAnzahl: 0,
              summeText: null,
              sepaDateiname: sepaDatei.name
            });
            return;
          }
          pruefDatei = umgestellt.datei;
        }

        const auswertungen = gueltige.filter(f => istAuswertungsPdfDatei(f.name));
        const rumpf = {
          modus: 'pruefen',
          customer_id: customer.id,
          ausgeloest_von: user?.email ?? null,
          sepa: { dateiname: sepaDatei.name, base64: await dateiZuBase64(pruefDatei) },
          weitere_sepa_lohn_gehalt: gueltige
            .filter(f => f !== sepaDatei && istSepaLohnGehaltDatei(f.name))
            .map(f => f.name),
          auswertungen: await Promise.all(
            auswertungen.map(async (datei) => ({
              dateiname: datei.name,
              base64: await dateiZuBase64(datei)
            }))
          )
        };

        const { data, error: aufrufFehler } = await supabase.functions.invoke('sepa-gehaltszahlungen', {
          body: rumpf
        });
        if (cancelled) return;

        if (aufrufFehler) {
          // Bewusst sperren statt durchlassen: ohne gültige Prüfung darf kein
          // Paket mit Zahlungsdaten den Kunden erreichen.
          setAuszahlungsliste({
            gesperrt: true,
            erfolgreich: false,
            probleme: [`Prüfung nicht erreichbar: ${aufrufFehler.message ?? 'unbekannter Fehler'}`],
            dateiname: null,
            zeilenAnzahl: 0,
            summeText: null,
            sepaDateiname: sepaDatei.name
          });
          return;
        }

        if (data?.nicht_zustaendig) {
          setAuszahlungsliste(null);
          setAuszahlungslisteDatei(null);
          return;
        }

        const probleme: string[] = Array.isArray(data?.probleme) ? data.probleme : [];

        // Verwendungszweck-Ersetzung aus derselben Antwort. Fehlt das Feld
        // (keine Regel hinterlegt oder ältere Function) oder betrifft die
        // Regel keine Zahlung dieser Datei, bleibt alles wie bisher und das
        // Modal zeigt nichts an. Die angepasste Datei wird nur zusammen mit
        // einer erzeugten Auszahlungsliste übernommen.
        const uebernehmeErsetzung = (mitAuszahlungsliste: boolean) => {
          const roh = data?.ersetzung;
          if (!roh || !roh.status || roh.status === 'keine_regel' || roh.status === 'nicht_enthalten') return;
          let status = String(roh.status) as VerwendungszweckErsetzung['status'];
          let gesperrt = Boolean(roh.gesperrt);
          let problemeErsetzung: string[] = Array.isArray(roh.probleme) ? roh.probleme.map(String) : [];
          let ersatz: File | null = null;
          if (status === 'ersetzt') {
            if (!mitAuszahlungsliste) {
              status = 'nicht_ausgefuehrt';
            } else if (typeof roh.sepa_base64 === 'string' && roh.sepa_base64 !== '') {
              ersatz = new globalThis.File([base64ZuBytes(roh.sepa_base64)], sepaDatei.name, {
                type: sepaDatei.type
              });
            } else {
              status = 'gesperrt';
              gesperrt = true;
              problemeErsetzung = ['Die angepasste SEPA-Datei fehlt in der Antwort der Prüfung.'];
            }
          }
          setErsetzung({
            status,
            gesperrt,
            probleme: problemeErsetzung,
            personalnummern: Array.isArray(roh.personalnummern) ? roh.personalnummern.map(String) : [],
            ersetzungen: status === 'ersetzt' && Array.isArray(roh.ersetzungen)
              ? roh.ersetzungen.map((eintrag: { personalnummer: unknown; position: unknown; betrag_text: unknown; neuer_text: unknown }) => ({
                  personalnummer: String(eintrag.personalnummer),
                  position: Number(eintrag.position),
                  betragText: String(eintrag.betrag_text),
                  neuerText: String(eintrag.neuer_text)
                }))
              : [],
            sepaDateiname: sepaDatei.name
          });
          if (ersatz) setErsetzteSepaDatei({ original: sepaDatei, ersatz });
        };

        if (!data?.ok || !data?.csv_base64 || !data?.dateiname) {
          setAuszahlungsliste({
            gesperrt: true,
            erfolgreich: false,
            probleme: probleme.length > 0 ? probleme : ['Die Auszahlungsliste konnte nicht erzeugt werden.'],
            dateiname: data?.dateiname ?? null,
            zeilenAnzahl: Number(data?.zeilen_anzahl ?? 0),
            summeText: data?.summe_text ?? null,
            sepaDateiname: sepaDatei.name
          });
          uebernehmeErsetzung(false);
          return;
        }

        // Der MIME-Typ wird aus der bestehenden Freigabeliste abgeleitet, damit
        // die erzeugte Datei dieselbe Validierung besteht wie jede hochgeladene.
        const csvMimeTyp = Object.keys(ALLOWED_MIME_TYPES).find(
          (typ) => ALLOWED_MIME_TYPES[typ] === 'csv'
        );
        if (!csvMimeTyp) {
          setAuszahlungsliste({
            gesperrt: true,
            erfolgreich: false,
            probleme: ['Für CSV ist kein erlaubter Dateityp hinterlegt, die Auszahlungsliste kann nicht angehängt werden.'],
            dateiname: data.dateiname,
            zeilenAnzahl: Number(data.zeilen_anzahl ?? 0),
            summeText: data.summe_text ?? null,
            sepaDateiname: sepaDatei.name
          });
          uebernehmeErsetzung(false);
          return;
        }

        const csvDatei = new globalThis.File([base64ZuBytes(data.csv_base64)], data.dateiname, {
          type: csvMimeTyp
        });

        setAuszahlungslisteDatei(csvDatei);
        uebernehmeErsetzung(true);
        setAuszahlungsliste({
          gesperrt: false,
          erfolgreich: true,
          probleme: [],
          dateiname: data.dateiname,
          zeilenAnzahl: Number(data.zeilen_anzahl ?? 0),
          summeText: data.summe_text ?? null,
          sepaDateiname: sepaDatei.name
        });
      } catch (err) {
        if (cancelled) return;
        setAuszahlungsliste({
          gesperrt: true,
          erfolgreich: false,
          probleme: [`Prüfung fehlgeschlagen: ${err instanceof Error ? err.message : 'unbekannter Fehler'}`],
          dateiname: null,
          zeilenAnzahl: 0,
          summeText: null,
          sepaDateiname: sepaDatei.name
        });
        setErsetzteSepaDatei(null);
      } finally {
        if (!cancelled) setAuszahlungslisteLaeuft(false);
      }
    };

    fuehreAus();

    return () => {
      cancelled = true;
    };
  }, [pendingFiles, kundenNummern, isOpen, customer.id, user?.email, sofortDateien, sofortTermin]);

  // Eine bestätigte Vollzugriff-Warnung darf nicht auf einen anderen
  // Empfänger oder eine andere Überschrift übergehen.
  useEffect(() => {
    setVollzugriffWarnungUebergehen(false);
  }, [selectedContactId, titelKategorie]);

  if (!isOpen) return null;

  // NEU: Abgeleitete Werte vom gewählten Kontakt
  const selectedContact = contacts.find(c => c.id === selectedContactId) || null;
  const hasPassword = selectedContact 
    ? !!selectedContact.secure_download_passwort && selectedContact.secure_download_passwort.trim() !== ''
    : false;

  // ============================================
  // DOWNLOADBEREICH FUNKTIONEN
  // ============================================

  const loadPreviewUploads = async (contactId: string) => {
    setPreviewLoading(true);
    setPreviewError(null);
    
    try {
      const uploads = await secureDocumentService.getUploadsForContact(contactId);
      setPreviewUploads(uploads);
    } catch (err) {
      console.error('Error loading preview uploads:', err);
      setPreviewError('Fehler beim Laden der Dokumente.');
    } finally {
      setPreviewLoading(false);
    }
  };

  // NEU (Zugriffsprotokoll): Historie für den gewählten Ansprechpartner laden.
  // Fehler sind non-blocking — der Downloadbereich bleibt nutzbar.
  const loadAccessLog = async (contactId: string) => {
    setAccessLogLoading(true);

    try {
      const entries = await secureDocumentService.getAccessLogForContact(contactId);
      setAccessLog(entries);
    } catch (err) {
      console.error('Error loading access log:', err);
    } finally {
      setAccessLogLoading(false);
    }
  };

  // Stufe 2: geplante und fehlgeschlagene Pakete für den Abschnitt
  // „Geplante Sendungen" laden. Fehler sind non-blocking (wie loadAccessLog).
  const loadScheduledUploads = async (contactId: string) => {
    setScheduledLoading(true);

    try {
      const uploads = await secureDocumentService.getScheduledUploadsForContact(contactId);
      setScheduledUploads(uploads);
    } catch (err) {
      console.error('Error loading scheduled uploads:', err);
    } finally {
      setScheduledLoading(false);
    }
  };

  const handlePreviewDownload = async (fileId: string, storagePath: string, filename: string) => {
    setDownloadingFiles(prev => new Set(prev).add(fileId));
    
    try {
      const signedUrl = await secureDocumentService.getSignedDownloadUrl(storagePath, filename);
      
      if (!signedUrl) {
        setPreviewError('Download-Link konnte nicht erstellt werden.');
        return;
      }

      const a = document.createElement('a');
      a.href = signedUrl;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download error:', err);
      setPreviewError('Download fehlgeschlagen.');
    } finally {
      setDownloadingFiles(prev => {
        const next = new Set(prev);
        next.delete(fileId);
        return next;
      });
    }
  };

  const handleDeleteUpload = async (uploadId: string) => {
    setDeletingUploadIds(prev => new Set(prev).add(uploadId));
    setPreviewError(null);

    try {
      const result = await secureDocumentService.deleteUpload(uploadId);

      if (!result.success) {
        setPreviewError(result.error || 'Löschen fehlgeschlagen.');
        return;
      }

      // Upload aus lokaler Liste entfernen
      setPreviewUploads(prev => prev.filter(u => u.id !== uploadId));
      // Stufe 2: fehlgeschlagene Pakete leben in der Geplant-Liste — dort ebenfalls entfernen
      setScheduledUploads(prev => prev.filter(u => u.id !== uploadId));
      setConfirmDeleteUploadId(null);
    } catch (err) {
      console.error('Delete upload error:', err);
      setPreviewError('Löschen fehlgeschlagen.');
    } finally {
      setDeletingUploadIds(prev => {
        const next = new Set(prev);
        next.delete(uploadId);
        return next;
      });
    }
  };

  // Stufe 2: geplante Sendung abbrechen. Löscht das Paket vollständig, solange
  // der Versand-Cron es nicht geclaimt hat (Claim-Guard im Service). Verliert
  // das Frontend das Rennen gegen den Cron, kommt eine verständliche Meldung
  // und die Liste wird neu geladen.
  const handleCancelScheduled = async (uploadId: string) => {
    setCancellingUploadIds(prev => new Set(prev).add(uploadId));
    setPreviewError(null);

    try {
      const result = await secureDocumentService.cancelScheduledUpload(uploadId);

      if (!result.success) {
        setPreviewError(result.error || 'Abbrechen fehlgeschlagen.');
        if (selectedContactId) {
          await loadScheduledUploads(selectedContactId);
        }
        return;
      }

      setScheduledUploads(prev => prev.filter(u => u.id !== uploadId));
      setConfirmCancelUploadId(null);
    } catch (err) {
      console.error('Cancel scheduled upload error:', err);
      setPreviewError('Abbrechen fehlgeschlagen.');
    } finally {
      setCancellingUploadIds(prev => {
        const next = new Set(prev);
        next.delete(uploadId);
        return next;
      });
    }
  };

  const formatGermanDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // NEU (Zugriffsprotokoll): Datum + Uhrzeit für die Protokoll-Einträge
  const formatGermanDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  };

  // Interne Zusatzzeile im Downloadbereich: Uhrzeit in derselben Ortszeit wie formatGermanDate
  const formatGermanTime = (dateString: string): string => {
    const date = new Date(dateString);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Folgepaket: eine Zeile der Änderungsliste (identisch zur Kundenseite).
  const beschreibeAenderung = (aenderung: FolgepaketAenderung): string => {
    let zeile = `${aenderung.titel || aenderung.dokument_typ}${aenderung.art === 'neu' ? ' (neu)' : ''}`;
    if (aenderung.personalnummern && aenderung.personalnummern.length > 0) {
      zeile += `: Personalnummern ${aenderung.personalnummern.join(', ')}`;
    }
    if (aenderung.kassen && aenderung.kassen.length > 0) {
      zeile += `: ${aenderung.kassen.join(', ')}`;
    }
    return zeile;
  };

  // Punkt 2 (Veralten-Tracking): veraltetes Paket in der Vorschau auf-/zuklappen.
  const toggleVeralteteVorschau = (uploadId: string) => {
    setExpandierteVeralteteVorschau(prev => {
      const next = new Set(prev);
      if (next.has(uploadId)) {
        next.delete(uploadId);
      } else {
        next.add(uploadId);
      }
      return next;
    });
  };

  // ============================================
  // SENDEN-TAB FUNKTIONEN (unverändert)
  // ============================================

  // Validiere eine einzelne Datei
  const validateFile = (file: File): { valid: boolean; error?: string } => {
    // Größe prüfen
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: `Datei zu groß (max. 2 GB)` };
    }

    // Dateityp prüfen
    const fileType = ALLOWED_MIME_TYPES[file.type];
    if (!fileType) {
      return { valid: false, error: `Dateityp nicht unterstützt` };
    }

    return { valid: true };
  };

  // Füge Dateien zur Liste hinzu
  const addFiles = (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const newPendingFiles: PendingUploadFile[] = [];

    for (const file of fileArray) {
      // Prüfe ob Datei bereits hinzugefügt wurde
      const alreadyAdded = pendingFiles.some(pf => pf.filename === file.name && pf.size === file.size);
      if (alreadyAdded) continue;

      const validation = validateFile(file);
      
      newPendingFiles.push({
        file,
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        filename: file.name,
        size: file.size,
        type: file.type,
        isValid: validation.valid,
        error: validation.error
      });
    }

    setPendingFiles(prev => [...prev, ...newPendingFiles]);
    setError(null);
  };

  // Entferne Datei aus der Liste
  const removeFile = (fileId: string) => {
    setPendingFiles(prev => prev.filter(f => f.id !== fileId));
  };

  // Drag & Drop Handler
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    if (e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  };

  // File Input Handler
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
      // Reset input damit gleiche Datei erneut ausgewählt werden kann
      e.target.value = '';
    }
  };

  // Berechne Gesamtgröße
  const totalSize = pendingFiles.reduce((sum, f) => sum + f.size, 0);
  const validFiles = pendingFiles.filter(f => f.isValid);

  // Hinweis auf große Dateien: Der Upload läuft als ein einziger Vorgang ohne
  // Wiederaufnahme. Ab LARGE_FILE_WARN_SIZE wird deshalb im Modal darauf
  // hingewiesen; die Dateien bleiben gültig und der Ablauf ändert sich nicht.
  const hatGrosseDateien = validFiles.some(f => f.size > LARGE_FILE_WARN_SIZE);

  // NEU (Überschrift): Standardfall-Erkennung + Bezugsmonat aus den Dateinamen.
  // Bei den vier Standardfällen wird der Monat aus dem Muster „_MM_JJJJ_" gelesen
  // (erster Treffer gewinnt). Weichen mehrere Treffer ab, gibt es einen nicht
  // blockierenden Hinweis. Findet sich in keinem Dateinamen ein Monat, greift der
  // manuelle Monat/Jahr-Fallback (Vormonat vorbelegt).
  const istStandardfall = titelKategorie === 'probe_1' || titelKategorie === 'probe_2'
    || titelKategorie === 'abrechnung_1' || titelKategorie === 'abrechnung_2';

  const dateiMonatsTreffer = validFiles
    .map(f => {
      const m = f.filename.match(MONATS_MUSTER);
      return m ? { monat: parseInt(m[1], 10), jahr: parseInt(m[2], 10) } : null;
    })
    .filter((x): x is { monat: number; jahr: number } => x !== null);

  const ersterMonatsTreffer = dateiMonatsTreffer.length > 0 ? dateiMonatsTreffer[0] : null;
  const monatsTrefferUneindeutig = new Set(dateiMonatsTreffer.map(t => `${t.jahr}-${t.monat}`)).size > 1;
  const monatAusDateiVorhanden = ersterMonatsTreffer !== null;

  // Effektiver Bezugsmonat für den Standardfall: aus Dateiname (erster Treffer)
  // oder — falls kein Treffer — aus dem manuellen Fallback-Feld.
  const effektiverMonat = istStandardfall
    ? (monatAusDateiVorhanden ? ersterMonatsTreffer!.monat : manualMonth)
    : null;
  const effektivesJahr = istStandardfall
    ? (monatAusDateiVorhanden ? ersterMonatsTreffer!.jahr : manualYear)
    : null;

  // Kundensichtbare Überschrift zusammensetzen. Standardfall: „<Kategorie> <Monat> <Jahr>"
  // (ohne Gedankenstrich); Sonstiges: freier Text ohne Monat.
  let finalerTitel = '';
  if (titelKategorie === 'sonstiges') {
    finalerTitel = titelSonstiges.trim();
  } else if (istStandardfall && effektiverMonat && effektivesJahr) {
    finalerTitel = `${TITEL_KATEGORIE_LABELS[titelKategorie]} ${MONATS_NAMEN[effektiverMonat - 1]} ${effektivesJahr}`;
  }
  const titelGueltig = finalerTitel !== '';

  // Warnung, wenn Unterlagen des Ersten Lohnlaufs an einen Ansprechpartner
  // ohne Vollzugriff gehen sollen. Gilt für jeden Kunden. Fehlt der Wert,
  // wird wie in der Datenbank (Default false) kein Vollzugriff angenommen.
  const vollzugriffWarnung = selectedContact !== null
    && VOLLZUGRIFF_WARN_KATEGORIEN.includes(titelKategorie)
    && !selectedContact.lohnschein_vollzugriff;

  // Jahr-Optionen für den manuellen Fallback (deckt Vormonat-im-Januar ab).
  const aktuellesJahr = new Date().getFullYear();
  const jahrOptionen = [aktuellesJahr + 1, aktuellesJahr, aktuellesJahr - 1, aktuellesJahr - 2];

  // Format Dateigröße
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  // Upload Handler
  const handleUpload = async () => {
    // Validierungen
    if (!selectedContactId || !selectedContact) {
      setError('Bitte wählen Sie einen Ansprechpartner aus.');
      return;
    }

    if (validFiles.length === 0) {
      setError('Bitte wählen Sie mindestens eine gültige Datei aus.');
      return;
    }

    if (totalSize > MAX_TOTAL_SIZE) {
      setError(`Gesamtgröße überschreitet das Limit von 2 GB (aktuell: ${formatFileSize(totalSize)})`);
      return;
    }

    if (!user?.email || !user?.name) {
      setError('Benutzerinformationen nicht verfügbar. Bitte neu anmelden.');
      return;
    }

    // Stufe 2: Absender muss gewählt sein (Außenidentität; Default ist per
    // Smart-Matching der eingeloggte Nutzer, sofern er ein gültiges Foto hat).
    if (!selectedSender) {
      setError('Bitte wählen Sie einen Absender aus.');
      return;
    }

    // NEU (Überschrift): Pflichtfeld — es muss eine gültige Überschrift vorliegen.
    if (!titelGueltig) {
      setError('Bitte wählen Sie eine Überschrift aus.');
      return;
    }

    // Stufe 2: bei terminiertem Versand ist ein Zeitpunkt Pflicht; die
    // Vergangenheits-Prüfung übernimmt der Service.
    if (scheduleMode === 'later' && !scheduledLocal) {
      setError('Bitte wählen Sie einen Sendezeitpunkt.');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      // Datei, die ohne sofortige Ausführung hochgeladen würde: die angepasste
      // SEPA-Datei aus der Verwendungszweck-Ersetzung (nur zusammen mit der
      // erzeugten Auszahlungsliste) oder das Original.
      const basisDatei = (datei: File): File =>
        ersetzteSepaDatei && auszahlungslisteDatei && datei === ersetzteSepaDatei.original
          ? ersetzteSepaDatei.ersatz
          : datei;

      // Sofortige Ausführung: den Ausführungstermin der ausgewählten
      // SEPA-Dateien vor allen weiteren Schritten auf den Versandtag umstellen.
      // Scheitert das, wird nichts protokolliert und nichts hochgeladen.
      const umgestellteDateien = new Map<File, File>();
      for (const pf of validFiles) {
        if (!sofortDateien.includes(pf.file.name)) continue;
        const umgestellt = await setzeAusfuehrungstermin(basisDatei(pf.file), versandtag);
        if (!umgestellt.ok) {
          setError(`Der Ausführungstermin von ${pf.filename} konnte nicht umgestellt werden: ${umgestellt.fehler} Es wurde nichts hochgeladen.`);
          setIsUploading(false);
          return;
        }
        umgestellteDateien.set(pf.file, umgestellt.datei);
      }

      // Auszahlungsliste: wurde sie erzeugt, geht sie als letzte Datei mit ins
      // Paket. Wurde die Sperre übergangen, existiert keine Datei und der
      // Vorgang wird festgehalten.
      if (auszahlungsliste?.gesperrt && auszahlungslisteUebergehen) {
        try {
          await supabase.functions.invoke('sepa-gehaltszahlungen', {
            body: {
              modus: 'protokoll',
              customer_id: customer.id,
              sepa_dateiname: auszahlungsliste.sepaDateiname,
              probleme: auszahlungsliste.probleme,
              ausgeloest_von: user.email
            }
          });
        } catch (protokollFehler) {
          console.warn('[Auszahlungsliste] Protokollierung fehlgeschlagen (non-blocking):', protokollFehler);
        }
      }

      // Verwendungszweck-Ersetzung: Entfällt sie, obwohl eine Regel hinterlegt
      // ist, wird das festgehalten. Das gilt für eine übergangene Sperre der
      // Ersetzung und für ein Paket, das ohne Auszahlungsliste rausgeht, weil
      // die Ersetzung dann nicht gelaufen ist.
      const ersetzungEntfaellt = ersetzung !== null && (
        (ersetzung.gesperrt && ersetzungUebergehen) ||
        (ersetzung.status === 'nicht_ausgefuehrt' && (auszahlungsliste?.gesperrt ?? false) && auszahlungslisteUebergehen)
      );
      if (ersetzung && ersetzungEntfaellt) {
        try {
          await supabase.functions.invoke('sepa-gehaltszahlungen', {
            body: {
              modus: 'ersetzung_protokoll',
              customer_id: customer.id,
              sepa_dateiname: ersetzung.sepaDateiname,
              probleme: ersetzung.gesperrt
                ? ersetzung.probleme
                : [`Paket ohne Auszahlungsliste versendet, Verwendungszweck für Personalnummer ${ersetzung.personalnummern.join(', ')} nicht ersetzt`],
              ausgeloest_von: user.email
            }
          });
        } catch (protokollFehler) {
          console.warn('[Verwendungszweck] Protokollierung fehlgeschlagen (non-blocking):', protokollFehler);
        }
      }

      // Die angepasste SEPA-Datei tritt an die Stelle des geprüften Originals,
      // aber nur zusammen mit der erzeugten Auszahlungsliste. Eine für die
      // sofortige Ausführung umgestellte Datei hat Vorrang; sie baut bereits
      // auf der angepassten Datei auf.
      const hochzuladendeDateien = validFiles.map(f => umgestellteDateien.get(f.file) ?? basisDatei(f.file));
      if (auszahlungslisteDatei) {
        hochzuladendeDateien.push(auszahlungslisteDatei);
      }

      const result = await secureDocumentService.createUploadWithFiles(
        customer.id,
        selectedContactId,
        hochzuladendeDateien,
        kommentar.trim() || null,
        user.email,
        user.name,
        {
          senderName: selectedSender.fullName,
          senderEmail: selectedSender.email,
          scheduledAtLocal: scheduleMode === 'later' ? scheduledLocal : null,
          titel: finalerTitel,
          // Punkt 2 (Veralten-Tracking): Paket-Typ + Periode aus der Kategorie
          // ableiten. "Sonstiges" liefert keinen Typ/keine Periode -> nimmt nie
          // am Veralten teil. Standardfälle nutzen den effektiven Bezugsmonat.
          paketTyp: MANUELL_PAKET_TYP[titelKategorie] ?? null,
          periodeJahr: MANUELL_PAKET_TYP[titelKategorie] ? effektivesJahr : null,
          periodeMonat: MANUELL_PAKET_TYP[titelKategorie] ? effektiverMonat : null
        }
      );

      if (!result.success) {
        setError(result.error || 'Upload fehlgeschlagen');
        setIsUploading(false);
        return;
      }

      // Stufe 2 — terminierter Versand: KEINE Sofort-Mail; das Paket ist als
      // 'geplant' angelegt, der Cron versendet zum Zeitpunkt. Bis dahin bleibt
      // es im Kunden-Downloadbereich unsichtbar und kann im Tab
      // „Downloadbereich" abgebrochen werden.
      if (scheduleMode === 'later') {
        onSuccess();
        onClose();
        return;
      }

      // E-Mail versenden
      try {
        const emailResponse = await netlifyFetch('/.netlify/functions/send-secure-document-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            upload_id: result.uploadId,
            // Stufe 2: Außenidentität — Signatur/Absendername/Duz-Gate der Mail
            // richten sich nach dem GEWÄHLTEN Absender (Bestand: user.email).
            sender_email: selectedSender.email,
            contact_id: selectedContactId
          })
        });

        const emailResult = await emailResponse.json();

        if (!emailResponse.ok || !emailResult.success) {
          // Upload war erfolgreich, aber E-Mail fehlgeschlagen
          console.error('E-Mail-Versand fehlgeschlagen:', emailResult.error);
          setError(`Upload erfolgreich, aber E-Mail konnte nicht versendet werden: ${emailResult.error || 'Unbekannter Fehler'}`);
          setIsUploading(false);
          return;
        }

        console.log('E-Mail erfolgreich versendet:', emailResult.messageId);
      } catch (emailErr) {
        console.error('E-Mail-Versand Fehler:', emailErr);
        setError(`Upload erfolgreich, aber E-Mail konnte nicht versendet werden: ${emailErr instanceof Error ? emailErr.message : 'Netzwerkfehler'}`);
        setIsUploading(false);
        return;
      }

      // Erfolg - Modal schließen und Callback aufrufen
      onSuccess();
      onClose();

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler beim Upload');
      setIsUploading(false);
    }
  };

  // Button deaktiviert?
  // Die Prüfung der Lohnlaufunterlagen sperrt den Upload, solange sie läuft
  // oder eine Beanstandung vorliegt. Konnte die Prüfung selbst nicht
  // durchlaufen, blockiert das bewusst nicht. Ist die Checkbox „Prüfung
  // übergehen" gesetzt, entfällt die Sperre für diesen einen Upload.
  // Dieselbe Mechanik gilt für die Auszahlungsliste: solange sie erzeugt wird
  // oder der Abgleich nicht aufgeht, bleibt der Upload gesperrt.
  // Ebenso sperrt eine beanstandete Verwendungszweck-Ersetzung, bis sie für
  // diesen Upload übergangen wird.
  // Die Vollzugriff-Warnung sperrt, bis der Versand für diesen Vorgang
  // ausdrücklich bestätigt wird.
  const isButtonDisabled = isUploading || !selectedContactId || !selectedSender || validFiles.length === 0 || totalSize > MAX_TOTAL_SIZE || (scheduleMode === 'later' && !scheduledLocal) || !titelGueltig || lohnlaufPruefungLaeuft || (!pruefungUebergehen && (lohnlaufPruefung?.hatSperre ?? false)) || auszahlungslisteLaeuft || (!auszahlungslisteUebergehen && (auszahlungsliste?.gesperrt ?? false)) || (!ersetzungUebergehen && (ersetzung?.gesperrt ?? false)) || (vollzugriffWarnung && !vollzugriffWarnungUebergehen);

  // ============================================
  // FILE TYPE ICON
  // ============================================

  const getFileTypeIcon = (filename: string): string => {
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    switch (ext) {
      case 'pdf': return '📄';
      case 'doc': case 'docx': return '📝';
      case 'xls': case 'xlsx': return '📊';
      case 'csv': return '📋';
      case 'jpg': case 'jpeg': case 'png': return '🖼️';
      case 'xml': return '📰';
      case 'zip': return '📦';
      default: return '📄';
    }
  };

  // ============================================
  // VORSCHAU-DATEILISTE (Kunden-Sicht, gruppiert)
  // ============================================
  // Spiegelt die externe Kundenseite (SecureDocumentAccessPage): bei
  // xtax_portal-Paketen (mindestens eine Datei mit Abschnitts-Titel) werden die
  // Dateien nach Abschnitt gruppiert und je Abschnitt eine Überschrift
  // eingezogen. Dateien ohne Abschnitt (manuelle Anhänge) landen unter
  // „Weitere Dokumente". Ohne jegliche Abschnitts-Zuordnung bleibt die Liste
  // flach wie bisher. Die Reihenfolge kommt unverändert aus dem Service.
  const renderPreviewFileList = (upload: SecureUploadWithFiles) => {
    const files = upload.files;
    const hasSections = files.some(f => !!explanationByFileId[f.id]?.section_titel);

    let lastSectionKey: string | null = null;
    let headerCount = 0;

    return files.map((file) => {
      let sectionHeader: React.ReactNode = null;

      if (hasSections) {
        const sectionTitel = explanationByFileId[file.id]?.section_titel || null;
        const effectiveKey = sectionTitel || '__weitere__';
        if (effectiveKey !== lastSectionKey) {
          lastSectionKey = effectiveKey;
          const isFirst = headerCount === 0;
          headerCount++;
          const headerLabel = sectionTitel || 'Weitere Dokumente';
          sectionHeader = (
            <div style={{
              fontSize: '12px',
              fontWeight: '600',
              color: '#374151',
              textTransform: 'uppercase',
              letterSpacing: '0.03em',
              margin: isFirst ? '0 0 2px 0' : '10px 0 2px 0'
            }}>
              {headerLabel}
            </div>
          );
        }
      }

      return (
        <React.Fragment key={file.id}>
          {sectionHeader}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 10px',
            backgroundColor: '#F9FAFB',
            borderRadius: '6px',
            border: '1px solid #E5E7EB'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: '16px' }}>{getFileTypeIcon(file.original_filename)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ 
                  fontSize: '13px', 
                  fontWeight: '500', 
                  color: '#374151', 
                  margin: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {file.original_filename}
                </p>
                {/* Stufe 3.1: DATEV-Dokumententyp-Beschreibung für xtax_portal-Files */}
                {explanationByFileId[file.id] && (
                  <>
                    <p style={{
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#374151',
                      margin: '3px 0 0 0',
                      lineHeight: '1.4'
                    }}>
                      {explanationByFileId[file.id].titel}
                    </p>
                    <p style={{
                      fontSize: '11px',
                      color: '#6B7280',
                      margin: '1px 0 0 0',
                      lineHeight: '1.4',
                      whiteSpace: 'pre-line'
                    }}>
                      {explanationByFileId[file.id].beschreibung}
                    </p>
                  </>
                )}
                <p style={{ fontSize: '11px', color: '#9CA3AF', margin: explanationByFileId[file.id] ? '2px 0 0 0' : 0 }}>
                  {formatFileSize(file.file_size)}
                </p>
              </div>
            </div>
            <button
              onClick={() => handlePreviewDownload(file.id, file.storage_path, file.original_filename)}
              disabled={downloadingFiles.has(file.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 10px',
                backgroundColor: downloadingFiles.has(file.id) ? '#E5E7EB' : '#EFF6FF',
                color: downloadingFiles.has(file.id) ? '#9CA3AF' : '#2563EB',
                border: `1px solid ${downloadingFiles.has(file.id) ? '#D1D5DB' : '#BFDBFE'}`,
                borderRadius: '4px',
                cursor: downloadingFiles.has(file.id) ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                fontWeight: '500',
                flexShrink: 0
              }}
            >
              <Download size={12} />
              {downloadingFiles.has(file.id) ? '...' : 'Download'}
            </button>
          </div>
        </React.Fragment>
      );
    });
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#FFFFFF',
        padding: '24px',
        borderRadius: '12px',
        maxWidth: '800px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: '1px solid #E5E7EB'
        }}>
          <h2 style={{ 
            fontSize: '20px', 
            fontWeight: 'bold', 
            color: '#111827',
            margin: 0
          }}>
            Geschützter Dokumentenbereich
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px'
            }}
            disabled={isUploading}
          >
            <X size={24} color="#6B7280" />
          </button>
        </div>

        {/* Customer Info */}
        <div style={{ 
          marginBottom: '16px', 
          padding: '12px', 
          backgroundColor: '#F9FAFB', 
          borderRadius: '8px',
          border: '1px solid #E5E7EB'
        }}>
          <p style={{ fontSize: '14px', color: '#374151', margin: 0, fontWeight: '500' }}>
            {customer.company}
          </p>
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          gap: '0',
          marginBottom: '20px',
          borderBottom: '2px solid #E5E7EB'
        }}>
          <button
            onClick={() => { setActiveTab('senden'); setError(null); }}
            disabled={isUploading}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '500',
              color: activeTab === 'senden' ? '#2563EB' : '#6B7280',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'senden' ? '2px solid #2563EB' : '2px solid transparent',
              marginBottom: '-2px',
              cursor: isUploading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s'
            }}
          >
            <Upload size={16} />
            Dokumente senden
          </button>
          <button
            onClick={() => { setActiveTab('downloadbereich'); setError(null); }}
            disabled={isUploading}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '500',
              color: activeTab === 'downloadbereich' ? '#2563EB' : '#6B7280',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'downloadbereich' ? '2px solid #2563EB' : '2px solid transparent',
              marginBottom: '-2px',
              cursor: isUploading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s'
            }}
          >
            <Eye size={16} />
            Downloadbereich
          </button>
        </div>

        {/* NEU: Ansprechpartner-Auswahl */}
        {contactsLoading ? (
          <div style={{ 
            marginBottom: '16px', 
            padding: '16px', 
            backgroundColor: '#F9FAFB', 
            borderRadius: '8px',
            border: '1px solid #E5E7EB',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
              Ansprechpartner werden geladen...
            </p>
          </div>
        ) : contacts.length === 0 ? (
          <div style={{ 
            marginBottom: '16px', 
            padding: '16px', 
            backgroundColor: '#FEF2F2', 
            borderRadius: '8px',
            border: '1px solid #FECACA',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <Users size={20} color="#DC2626" style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              <p style={{ 
                fontSize: '14px', 
                color: '#991B1B', 
                margin: '0 0 4px 0',
                fontWeight: '600'
              }}>
                Keine Ansprechpartner hinterlegt
              </p>
              <p style={{ 
                fontSize: '13px', 
                color: '#B91C1C', 
                margin: 0,
                lineHeight: '1.4'
              }}>
                Bitte legen Sie zuerst einen Ansprechpartner in den Kundenstammdaten an, 
                bevor Sie Dokumente versenden können.
              </p>
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ 
              display: 'block',
              fontSize: '14px', 
              fontWeight: '500', 
              color: '#374151',
              marginBottom: '6px'
            }}>
              Ansprechpartner
            </label>
            
            {contacts.length === 1 ? (
              /* Einzelner Kontakt: Nur Info-Anzeige */
              <div style={{ 
                padding: '10px 12px', 
                backgroundColor: '#F9FAFB', 
                borderRadius: '6px',
                border: '1px solid #D1D5DB'
              }}>
                <p style={{ fontSize: '14px', color: '#111827', margin: 0, fontWeight: '500' }}>
                  {customerContactService.getContactDisplayName(contacts[0])}
                  {contacts[0].rolle && (
                    <span style={{ fontWeight: 'normal', color: '#6B7280' }}> ({contacts[0].rolle})</span>
                  )}
                </p>
                <p style={{ fontSize: '13px', color: '#6B7280', margin: '2px 0 0 0' }}>
                  {contacts[0].email}{contacts[0].email_cc ? `, CC: ${contacts[0].email_cc}` : ''}
                </p>
              </div>
            ) : (
              /* Mehrere Kontakte: Dropdown */
              <>
                <select
                  value={selectedContactId || ''}
                  onChange={(e) => {
                    setSelectedContactId(e.target.value || null);
                    setError(null);
                    setPreviewError(null);
                  }}
                  disabled={isUploading}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px',
                    color: selectedContactId ? '#374151' : '#9CA3AF',
                    backgroundColor: '#FFFFFF',
                    boxSizing: 'border-box',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">Bitte Ansprechpartner wählen...</option>
                  {contacts.map(contact => (
                    <option key={contact.id} value={contact.id}>
                      {customerContactService.getContactDisplayNameWithRole(contact)} — {contact.email}
                    </option>
                  ))}
                </select>
                
                {/* Gewählter Kontakt: Detail-Info */}
                {selectedContact && (
                  <div style={{ 
                    marginTop: '8px',
                    padding: '10px 12px', 
                    backgroundColor: '#F0FDF4', 
                    borderRadius: '6px',
                    border: '1px solid #BBF7D0'
                  }}>
                    <p style={{ fontSize: '13px', color: '#166534', margin: 0 }}>
                      Empfänger: {selectedContact.email}
                      {selectedContact.email_cc ? `, CC: ${selectedContact.email_cc}` : ''}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ============================================ */}
        {/* TAB: DOKUMENTE SENDEN */}
        {/* ============================================ */}
        {activeTab === 'senden' && (
          <>
            {/* Stufe 2: Absender-Auswahl „Senden als andere Person" */}
            <SenderSelection
              senders={senders}
              selectedSender={selectedSender}
              onSenderChange={setSelectedSender}
              disabled={isUploading}
              loading={sendersLoading}
              error={sendersError}
              photoValidationErrors={photoValidationErrors}
            />

            {/* Hinweis: Noch kein Passwort. Blockiert den Versand nicht; die
                Versandmail enthält in diesem Fall den Link zum Festlegen. */}
            {selectedContact && !hasPassword && (
              <div style={{ 
                marginBottom: '16px', 
                padding: '16px', 
                backgroundColor: '#FFFBEB', 
                borderRadius: '8px',
                border: '1px solid #FDE68A',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px'
              }}>
                <Lock size={20} color="#D97706" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <p style={{ 
                    fontSize: '14px', 
                    color: '#92400E', 
                    margin: '0 0 4px 0',
                    fontWeight: '600'
                  }}>
                    Noch kein Passwort hinterlegt
                  </p>
                  <p style={{ 
                    fontSize: '13px', 
                    color: '#B45309', 
                    margin: 0,
                    lineHeight: '1.4'
                  }}>
                    Der Versand ist möglich. Die Versandmail enthält zusätzlich einen Link, 
                    über den der Ansprechpartner sein Passwort festlegt. Die Dokumente kann er erst danach öffnen.
                  </p>
                </div>
              </div>
            )}

            {/* Drag & Drop Zone */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                marginBottom: '16px',
                padding: '32px 24px',
                border: `2px dashed ${isDragOver ? '#3B82F6' : '#D1D5DB'}`,
                borderRadius: '8px',
                backgroundColor: isDragOver ? '#EFF6FF' : '#F9FAFB',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.2s ease'
              }}
            >
              <Upload size={32} color={isDragOver ? '#3B82F6' : '#9CA3AF'} style={{ marginBottom: '12px' }} />
              <p style={{ 
                fontSize: '14px', 
                color: '#374151', 
                margin: '0 0 4px 0',
                fontWeight: '500'
              }}>
                Dateien hierher ziehen oder klicken zum Auswählen
              </p>
              <p style={{ 
                fontSize: '12px', 
                color: '#6B7280', 
                margin: 0 
              }}>
                PDF, Word, Excel, CSV, Bilder, XML, ZIP • Max. 2 GB pro Datei
              </p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png,.xml,.zip"
              />
            </div>

            {/* Dateiliste */}
            {pendingFiles.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '8px' 
                }}>
                  {pendingFiles.map((pf) => (
                    <div key={pf.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      backgroundColor: pf.isValid ? '#F9FAFB' : '#FEF2F2',
                      borderRadius: '6px',
                      border: `1px solid ${pf.isValid ? '#E5E7EB' : '#FECACA'}`
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                        <File size={18} color={pf.isValid ? '#6B7280' : '#DC2626'} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ 
                            fontSize: '13px', 
                            fontWeight: '500', 
                            color: pf.isValid ? '#374151' : '#991B1B', 
                            margin: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {pf.filename}
                          </p>
                          <p style={{ 
                            fontSize: '11px', 
                            color: pf.isValid ? '#9CA3AF' : '#B91C1C', 
                            margin: 0 
                          }}>
                            {pf.isValid ? formatFileSize(pf.size) : pf.error}
                          </p>
                          {/* Hinweis bei großen Dateien: der Upload dauert entsprechend
                              lange und kann nicht fortgesetzt werden. */}
                          {pf.isValid && pf.size > LARGE_FILE_WARN_SIZE && (
                            <p style={{
                              fontSize: '11px',
                              color: '#B45309',
                              margin: '2px 0 0 0',
                              lineHeight: '1.4'
                            }}>
                              Große Datei: Der Upload kann mehrere Minuten dauern.
                            </p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(pf.id);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Trash2 size={16} color="#9CA3AF" />
                      </button>
                    </div>
                  ))}
                </div>
                {/* Gesamtgröße */}
                <p style={{ 
                  fontSize: '12px', 
                  color: totalSize > MAX_TOTAL_SIZE ? '#DC2626' : '#6B7280', 
                  margin: '8px 0 0 0',
                  textAlign: 'right'
                }}>
                  Gesamt: {formatFileSize(totalSize)} / 2 GB
                </p>
              </div>
            )}

            {/* Prüfung der DATEV-Lohnlaufunterlagen. Erscheint nur, wenn in der
                Auswahl Dateien nach DATEV-Muster erkannt wurden. */}
            <LohnlaufPruefungPanel
              ergebnis={lohnlaufPruefung}
              laeuft={lohnlaufPruefungLaeuft}
              sperreAufgehoben={pruefungUebergehen}
            />

            {/* Übergehen der Sperre. Erscheint nur bei einer Beanstandung. Die
                Befunde bleiben unverändert stehen, freigegeben wird lediglich der
                Upload-Button. Die Einstellung gilt nur für diesen Upload und ist
                beim nächsten Öffnen des Modals wieder inaktiv. */}
            {!lohnlaufPruefungLaeuft && lohnlaufPruefung?.hatSperre && (
              <div style={{
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#FFFBEB',
                borderRadius: '8px',
                border: '1px solid #FDE68A'
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  cursor: isUploading ? 'not-allowed' : 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={pruefungUebergehen}
                    onChange={(e) => { setPruefungUebergehen(e.target.checked); setError(null); }}
                    disabled={isUploading}
                    style={{
                      width: '16px',
                      height: '16px',
                      marginTop: '2px',
                      flexShrink: 0,
                      cursor: isUploading ? 'not-allowed' : 'pointer'
                    }}
                  />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#92400E' }}>
                      Prüfung übergehen und Upload freigeben
                    </span>
                    <span style={{ display: 'block', fontSize: '12px', color: '#92400E', marginTop: '2px', lineHeight: '1.4' }}>
                      Die Beanstandungen bleiben stehen. Gilt nur für diesen Upload.
                    </span>
                  </span>
                </label>
              </div>
            )}

            {/* Sofortige Ausführung. Erscheint, wenn eine SEPA-Datei laut
                DATEV erst nach dem heutigen Tag ausgeführt würde. Angehakte
                Dateien werden beim Upload auf den Versandtag umgestellt;
                geändert wird nur der Ausführungstermin. Die Auswahl gilt nur
                für diesen Upload und ist beim nächsten Öffnen wieder inaktiv. */}
            {lohnlaufPruefung && lohnlaufPruefung.spaeterAuszufuehren.length > 0 && (
              <div style={{
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#F0F9FF',
                borderRadius: '8px',
                border: '1px solid #BAE6FD'
              }}>
                <p style={{ fontSize: '13px', fontWeight: '600', color: '#075985', margin: '0 0 2px 0' }}>
                  Sofort ausführen
                </p>
                <p style={{ fontSize: '12px', color: '#075985', margin: 0, lineHeight: '1.4' }}>
                  Bei angehakten Dateien setzt das Portal den Ausführungstermin auf den Versandtag ({isoZuDeutsch(versandtag)}).
                  Alle übrigen Angaben bleiben unverändert. Der Begleitzettel im PDF nennt weiterhin den ursprünglichen Termin.
                </p>
                {lohnlaufPruefung.spaeterAuszufuehren.map((eintrag) => (
                  <label key={eintrag.dateiname} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    marginTop: '10px',
                    cursor: isUploading || lohnlaufPruefungLaeuft ? 'not-allowed' : 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={sofortDateien.includes(eintrag.dateiname)}
                      onChange={(e) => {
                        const angehakt = e.target.checked;
                        setSofortDateien(vorher => angehakt
                          ? (vorher.includes(eintrag.dateiname) ? vorher : vorher.concat([eintrag.dateiname]))
                          : vorher.filter(name => name !== eintrag.dateiname));
                        setError(null);
                      }}
                      disabled={isUploading || lohnlaufPruefungLaeuft}
                      style={{
                        width: '16px',
                        height: '16px',
                        marginTop: '2px',
                        flexShrink: 0,
                        cursor: isUploading || lohnlaufPruefungLaeuft ? 'not-allowed' : 'pointer'
                      }}
                    />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#075985' }}>
                        {eintrag.zahlungsart}: sofort ausführen statt am {isoZuDeutsch(eintrag.termin)}
                      </span>
                      <span style={{ display: 'block', fontSize: '12px', color: '#0369A1', marginTop: '2px', lineHeight: '1.4', wordBreak: 'break-word' }}>
                        {eintrag.dateiname}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            )}

            {/* Auszahlungsliste (OPPM). Erscheint nur, wenn in der Auswahl eine
                SEPA-Überweisungsdatei für Lohn und Gehalt liegt. */}
            {auszahlungslisteLaeuft && (
              <div style={{
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#F9FAFB',
                borderRadius: '8px',
                border: '1px solid #E5E7EB'
              }}>
                <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>
                  Auszahlungsliste wird erstellt und gegen die SEPA-Datei abgeglichen...
                </p>
              </div>
            )}

            {!auszahlungslisteLaeuft && auszahlungsliste?.erfolgreich && (
              <div style={{
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#F0FDF4',
                borderRadius: '8px',
                border: '1px solid #BBF7D0'
              }}>
                <p style={{ fontSize: '13px', fontWeight: '600', color: '#166534', margin: '0 0 2px 0' }}>
                  Auszahlungsliste wird mitgesendet: {auszahlungsliste.dateiname}
                </p>
                <p style={{ fontSize: '12px', color: '#166534', margin: 0, lineHeight: '1.4' }}>
                  {auszahlungsliste.zeilenAnzahl} Zahlung{auszahlungsliste.zeilenAnzahl !== 1 ? 'en' : ''}
                  {auszahlungsliste.summeText ? `, Summe ${auszahlungsliste.summeText} EUR` : ''}
                  {' · '}Anzahl und Summe stimmen mit der SEPA-Datei überein.
                </p>
              </div>
            )}

            {!auszahlungslisteLaeuft && auszahlungsliste?.gesperrt && (
              <div style={{
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#FEF2F2',
                borderRadius: '8px',
                border: '1px solid #FECACA'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <AlertCircle size={18} color="#DC2626" style={{ flexShrink: 0, marginTop: '1px' }} />
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontSize: '13px', fontWeight: '600', color: '#991B1B', margin: '0 0 4px 0' }}>
                      Auszahlungsliste konnte nicht erstellt werden
                    </p>
                    <ul style={{ margin: 0, paddingLeft: '18px' }}>
                      {auszahlungsliste.probleme.map((problem, index) => (
                        <li key={index} style={{ fontSize: '12px', color: '#B91C1C', lineHeight: '1.5' }}>
                          {problem}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <label style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  marginTop: '12px',
                  cursor: isUploading ? 'not-allowed' : 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={auszahlungslisteUebergehen}
                    onChange={(e) => { setAuszahlungslisteUebergehen(e.target.checked); setError(null); }}
                    disabled={isUploading}
                    style={{
                      width: '16px',
                      height: '16px',
                      marginTop: '2px',
                      flexShrink: 0,
                      cursor: isUploading ? 'not-allowed' : 'pointer'
                    }}
                  />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#991B1B' }}>
                      Ohne Auszahlungsliste senden
                    </span>
                    <span style={{ display: 'block', fontSize: '12px', color: '#B91C1C', marginTop: '2px', lineHeight: '1.4' }}>
                      Das Paket geht ohne die CSV raus. Der Vorgang wird protokolliert und gilt nur für diesen Upload.
                    </span>
                  </span>
                </label>
              </div>
            )}

            {/* Verwendungszweck-Ersetzung (OPPM). Erscheint nur, wenn ein
                hinterlegter Verwendungszweck ersetzt wurde (grün) oder die
                Ersetzung den Upload sperrt (rot). */}
            {!auszahlungslisteLaeuft && ersetzung?.status === 'ersetzt' && (
              <div style={{
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#F0FDF4',
                borderRadius: '8px',
                border: '1px solid #BBF7D0'
              }}>
                <p style={{ fontSize: '13px', fontWeight: '600', color: '#166534', margin: '0 0 2px 0' }}>
                  Verwendungszweck ersetzt in {ersetzung.sepaDateiname}
                </p>
                {ersetzung.ersetzungen.map((eintrag, index) => (
                  <p key={index} style={{ fontSize: '12px', color: '#166534', margin: 0, lineHeight: '1.4' }}>
                    Personalnummer {eintrag.personalnummer}, {eintrag.betragText} EUR: „{eintrag.neuerText}“
                  </p>
                ))}
                <p style={{ fontSize: '12px', color: '#166534', margin: '2px 0 0 0', lineHeight: '1.4' }}>
                  Hochgeladen wird die angepasste Datei. Beträge, Konten und alle übrigen Zahlungen bleiben unverändert.
                </p>
              </div>
            )}

            {!auszahlungslisteLaeuft && ersetzung?.gesperrt && (
              <div style={{
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#FEF2F2',
                borderRadius: '8px',
                border: '1px solid #FECACA'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <AlertCircle size={18} color="#DC2626" style={{ flexShrink: 0, marginTop: '1px' }} />
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontSize: '13px', fontWeight: '600', color: '#991B1B', margin: '0 0 4px 0' }}>
                      Verwendungszweck konnte nicht ersetzt werden
                    </p>
                    <ul style={{ margin: 0, paddingLeft: '18px' }}>
                      {ersetzung.probleme.map((problem, index) => (
                        <li key={index} style={{ fontSize: '12px', color: '#B91C1C', lineHeight: '1.5' }}>
                          {problem}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <label style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  marginTop: '12px',
                  cursor: isUploading ? 'not-allowed' : 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={ersetzungUebergehen}
                    onChange={(e) => { setErsetzungUebergehen(e.target.checked); setError(null); }}
                    disabled={isUploading}
                    style={{
                      width: '16px',
                      height: '16px',
                      marginTop: '2px',
                      flexShrink: 0,
                      cursor: isUploading ? 'not-allowed' : 'pointer'
                    }}
                  />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#991B1B' }}>
                      Ohne Ersetzung senden
                    </span>
                    <span style={{ display: 'block', fontSize: '12px', color: '#B91C1C', marginTop: '2px', lineHeight: '1.4' }}>
                      Die SEPA-Datei geht unverändert raus. Der Vorgang wird protokolliert und gilt nur für diesen Upload.
                    </span>
                  </span>
                </label>
              </div>
            )}

            {/* NEU (Überschrift): Pflicht-Überschrift des Pakets. Standardfälle
                setzen einen strukturierten Titel mit Bezugsmonat (Monat aus dem
                Dateinamen; sonst manueller Fallback), „Sonstiges" erlaubt freien
                Text ohne Monat. Die fertige Überschrift wird als Vorschau gezeigt. */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block',
                fontSize: '14px', 
                fontWeight: '500', 
                color: '#374151',
                marginBottom: '6px'
              }}>
                Überschrift
              </label>
              <select
                value={titelKategorie}
                onChange={(e) => { setTitelKategorie(e.target.value as TitelKategorie); setError(null); }}
                disabled={isUploading}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: titelKategorie ? '#374151' : '#9CA3AF',
                  backgroundColor: '#FFFFFF',
                  boxSizing: 'border-box',
                  cursor: 'pointer'
                }}
              >
                <option value="">Bitte Überschrift wählen...</option>
                <option value="probe_1">Probeabrechnung Erster Lohnlauf</option>
                <option value="probe_2">Probeabrechnung Zweiter Lohnlauf</option>
                <option value="abrechnung_1">Abrechnung Erster Lohnlauf</option>
                <option value="abrechnung_2">Abrechnung Zweiter Lohnlauf</option>
                <option value="sonstiges">Sonstiges</option>
              </select>

              {/* Standardfall: manueller Bezugsmonat, falls kein Monat im Dateinamen */}
              {istStandardfall && !monatAusDateiVorhanden && (
                <div style={{ marginTop: '8px' }}>
                  <p style={{ fontSize: '12px', color: '#B45309', margin: '0 0 6px 0', lineHeight: '1.4' }}>
                    Aus den Dateinamen konnte kein Monat erkannt werden. Bitte Bezugsmonat wählen.
                  </p>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <select
                      value={manualMonth}
                      onChange={(e) => setManualMonth(parseInt(e.target.value, 10))}
                      disabled={isUploading}
                      style={{
                        flex: 1,
                        padding: '8px 10px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        fontSize: '14px',
                        color: '#374151',
                        backgroundColor: '#FFFFFF',
                        boxSizing: 'border-box',
                        cursor: 'pointer'
                      }}
                    >
                      {MONATS_NAMEN.map((name, idx) => (
                        <option key={idx} value={idx + 1}>{name}</option>
                      ))}
                    </select>
                    <select
                      value={manualYear}
                      onChange={(e) => setManualYear(parseInt(e.target.value, 10))}
                      disabled={isUploading}
                      style={{
                        flex: 1,
                        padding: '8px 10px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        fontSize: '14px',
                        color: '#374151',
                        backgroundColor: '#FFFFFF',
                        boxSizing: 'border-box',
                        cursor: 'pointer'
                      }}
                    >
                      {jahrOptionen.map(j => (
                        <option key={j} value={j}>{j}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Standardfall: Hinweis bei uneindeutigen Monaten in den Dateinamen */}
              {istStandardfall && monatsTrefferUneindeutig && (
                <p style={{ fontSize: '12px', color: '#B45309', margin: '8px 0 0 0', lineHeight: '1.4' }}>
                  Hinweis: Die hochgeladenen Dateien enthalten unterschiedliche Monate. Verwendet wird
                  der erste Treffer — bitte die Überschrift unten prüfen.
                </p>
              )}

              {/* Sonstiges: freier Titel ohne Monat */}
              {titelKategorie === 'sonstiges' && (
                <input
                  type="text"
                  value={titelSonstiges}
                  onChange={(e) => { setTitelSonstiges(e.target.value); setError(null); }}
                  placeholder="Überschrift eingeben..."
                  disabled={isUploading}
                  style={{
                    width: '100%',
                    marginTop: '8px',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px',
                    color: '#374151',
                    boxSizing: 'border-box'
                  }}
                />
              )}

              {/* Vorschau der kundensichtbaren Überschrift */}
              {titelGueltig && (
                <div style={{
                  marginTop: '8px',
                  padding: '8px 10px',
                  backgroundColor: '#F0FDF4',
                  borderRadius: '6px',
                  border: '1px solid #BBF7D0'
                }}>
                  <p style={{ fontSize: '13px', color: '#166534', margin: 0 }}>
                    Überschrift für den Kunden: <strong>{finalerTitel}</strong>
                  </p>
                </div>
              )}
            </div>

            {/* Warnung: Unterlagen des Ersten Lohnlaufs an einen Ansprechpartner
                ohne Vollzugriff. Der Upload bleibt gesperrt, bis der Versand für
                diesen Vorgang bestätigt wird. */}
            {vollzugriffWarnung && selectedContact && (
              <div style={{
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#FEF2F2',
                borderRadius: '8px',
                border: '1px solid #FECACA'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <AlertCircle size={18} color="#DC2626" style={{ flexShrink: 0, marginTop: '1px' }} />
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontSize: '13px', fontWeight: '600', color: '#991B1B', margin: '0 0 4px 0' }}>
                      Ansprechpartner ohne Vollzugriff
                    </p>
                    <p style={{ fontSize: '12px', color: '#B91C1C', margin: 0, lineHeight: '1.5' }}>
                      {customerContactService.getContactDisplayName(selectedContact)} hat in den Stammdaten keinen
                      Vollzugriff. Bitte prüfen Sie vor dem Versand von „{TITEL_KATEGORIE_LABELS[titelKategorie]}“,
                      ob der Ansprechpartner diese Unterlagen erhalten darf.
                    </p>
                  </div>
                </div>

                <label style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  marginTop: '12px',
                  cursor: isUploading ? 'not-allowed' : 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={vollzugriffWarnungUebergehen}
                    onChange={(e) => { setVollzugriffWarnungUebergehen(e.target.checked); setError(null); }}
                    disabled={isUploading}
                    style={{
                      width: '16px',
                      height: '16px',
                      marginTop: '2px',
                      flexShrink: 0,
                      cursor: isUploading ? 'not-allowed' : 'pointer'
                    }}
                  />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#991B1B' }}>
                      Trotzdem an diesen Ansprechpartner senden
                    </span>
                    <span style={{ display: 'block', fontSize: '12px', color: '#B91C1C', marginTop: '2px', lineHeight: '1.4' }}>
                      Gilt nur für diesen Upload und diesen Ansprechpartner.
                    </span>
                  </span>
                </label>
              </div>
            )}

            {/* Kommentar */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block',
                fontSize: '14px', 
                fontWeight: '500', 
                color: '#374151',
                marginBottom: '6px'
              }}>
                Nachricht an den Kunden (optional)
              </label>
              <textarea
                value={kommentar}
                onChange={(e) => setKommentar(e.target.value)}
                placeholder="Zusätzliche Informationen für den Kunden..."
                disabled={isUploading}
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '10px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#374151',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Stufe 2: Versandzeitpunkt (Sofort / Geplant) */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block',
                fontSize: '14px', 
                fontWeight: '500', 
                color: '#374151',
                marginBottom: '6px'
              }}>
                Versandzeitpunkt
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <div style={{
                  display: 'inline-flex',
                  backgroundColor: '#F3F4F6',
                  borderRadius: '6px',
                  padding: '3px'
                }}>
                  <button
                    type="button"
                    onClick={() => setScheduleMode('now')}
                    disabled={isUploading}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '5px 10px',
                      backgroundColor: scheduleMode === 'now' ? '#FFFFFF' : 'transparent',
                      color: scheduleMode === 'now' ? '#2563EB' : '#6B7280',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: scheduleMode === 'now' ? 600 : 500,
                      cursor: isUploading ? 'not-allowed' : 'pointer',
                      boxShadow: scheduleMode === 'now' ? '0 1px 2px 0 rgba(0,0,0,0.06)' : 'none'
                    }}
                  >
                    Sofort senden
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setScheduleMode('later');
                      // Vorbelegung Folgetag 06:30 Uhr, ein bereits gewählter
                      // Zeitpunkt bleibt unverändert.
                      if (!scheduledLocal) {
                        setScheduledLocal(berlinTomorrowLocalString(STANDARD_VERSAND_STUNDE, STANDARD_VERSAND_MINUTE));
                      }
                    }}
                    disabled={isUploading}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '5px 10px',
                      backgroundColor: scheduleMode === 'later' ? '#FFFFFF' : 'transparent',
                      color: scheduleMode === 'later' ? '#2563EB' : '#6B7280',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: scheduleMode === 'later' ? 600 : 500,
                      cursor: isUploading ? 'not-allowed' : 'pointer',
                      boxShadow: scheduleMode === 'later' ? '0 1px 2px 0 rgba(0,0,0,0.06)' : 'none'
                    }}
                  >
                    <Clock size={13} />
                    Geplant senden
                  </button>
                </div>
                {scheduleMode === 'later' && (
                  <input
                    type="datetime-local"
                    value={scheduledLocal}
                    min={berlinNowLocalString()}
                    onChange={(e) => setScheduledLocal(e.target.value)}
                    disabled={isUploading}
                    style={{
                      padding: '6px 8px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: '#374151',
                      backgroundColor: '#FFFFFF'
                    }}
                  />
                )}
              </div>
              {scheduleMode === 'later' && (
                <p style={{ fontSize: '12px', color: '#6B7280', margin: '6px 0 0 0', lineHeight: '1.4' }}>
                  Die Dateien werden jetzt hochgeladen; E-Mail und Sichtbarkeit im Downloadbereich
                  folgen erst zum gewählten Zeitpunkt (Berliner Zeit). Bis dahin kann die Sendung
                  im Tab „Downloadbereich" abgebrochen werden.
                </p>
              )}
            </div>

            {/* Info-Box */}
            <div style={{ 
              marginBottom: '16px', 
              padding: '12px', 
              backgroundColor: '#F0F9FF', 
              borderRadius: '8px',
              border: '1px solid #BAE6FD'
            }}>
              <p style={{ fontSize: '13px', color: '#075985', margin: 0, lineHeight: '1.5' }}>
                Der Ansprechpartner erhält eine E-Mail mit einem Link zu seinem persönlichen, geschützten Downloadbereich. 
                Der Zugang erfolgt über das individuell hinterlegte Passwort.
              </p>
            </div>

            {/* Hinweis auf große Dateien: erscheint, sobald mindestens eine gültige
                Datei über der Warnschwelle liegt. Der Upload bleibt möglich; der
                Hinweis erklärt nur, worauf während des Vorgangs zu achten ist. */}
            {hatGrosseDateien && (
              <div style={{ 
                marginBottom: '16px', 
                padding: '12px', 
                backgroundColor: '#FFFBEB', 
                borderRadius: '8px',
                border: '1px solid #FDE68A',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px'
              }}>
                <AlertCircle size={18} color="#D97706" style={{ flexShrink: 0, marginTop: '1px' }} />
                <div>
                  <p style={{ 
                    fontSize: '13px', 
                    color: '#92400E', 
                    margin: '0 0 4px 0',
                    fontWeight: '600'
                  }}>
                    Große Dateien im Paket
                  </p>
                  <p style={{ 
                    fontSize: '13px', 
                    color: '#92400E', 
                    margin: 0,
                    lineHeight: '1.5'
                  }}>
                    Mindestens eine Datei ist größer als 25 MB. Der Upload läuft in einem Zug und
                    kann nicht fortgesetzt werden. Bitte lassen Sie das Fenster geöffnet, bis der
                    Vorgang abgeschlossen ist. Bricht die Verbindung ab, muss der Upload komplett
                    wiederholt werden.
                  </p>
                </div>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div style={{ 
                marginBottom: '16px', 
                padding: '12px', 
                backgroundColor: '#FEF2F2', 
                borderRadius: '8px',
                border: '1px solid #FECACA',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <AlertCircle size={16} color="#DC2626" />
                <span style={{ fontSize: '14px', color: '#DC2626' }}>{error}</span>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={onClose}
                disabled={isUploading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Abbrechen
              </button>
              <button
                onClick={handleUpload}
                disabled={isButtonDisabled}
                style={{
                  padding: '8px 16px',
                  backgroundColor: isButtonDisabled ? '#9CA3AF' : '#3B82F6',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isButtonDisabled ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {isUploading
                  ? 'Wird hochgeladen...'
                  : scheduleMode === 'later'
                    ? `${validFiles.length} Datei${validFiles.length !== 1 ? 'en' : ''} hochladen & Versand planen`
                    : `${validFiles.length} Datei${validFiles.length !== 1 ? 'en' : ''} hochladen`}
              </button>
            </div>
          </>
        )}

        {/* ============================================ */}
        {/* TAB: DOWNLOADBEREICH (Kunden-Sicht) */}
        {/* ============================================ */}
        {activeTab === 'downloadbereich' && (
          <>
            {/* Stufe 2: Geplante Sendungen (Mitarbeiter-Sicht) — erscheinen
                bewusst OBERHALB der Kundensicht; der Kunde sieht diese Pakete
                erst nach erfolgreichem Versand. */}
            {selectedContactId && (scheduledLoading || scheduledUploads.length > 0) && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginBottom: '8px'
                }}>
                  <Clock size={16} color="#374151" />
                  <p style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: 0 }}>
                    Geplante Sendungen
                  </p>
                </div>
                {scheduledLoading ? (
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#F9FAFB',
                    borderRadius: '8px',
                    border: '1px solid #E5E7EB'
                  }}>
                    <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>
                      Geplante Sendungen werden geladen...
                    </p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {scheduledUploads.map((upload) => {
                      const isFailed = upload.versand_status === 'fehler';
                      const busy = cancellingUploadIds.has(upload.id) || deletingUploadIds.has(upload.id);
                      const confirmOpen = isFailed
                        ? confirmDeleteUploadId === upload.id
                        : confirmCancelUploadId === upload.id;
                      return (
                        <div key={upload.id} style={{
                          padding: '12px',
                          backgroundColor: isFailed ? '#FEF2F2' : '#EFF6FF',
                          borderRadius: '8px',
                          border: `1px solid ${isFailed ? '#FECACA' : '#BFDBFE'}`
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '14px',
                                fontWeight: '600',
                                color: isFailed ? '#991B1B' : '#1E3A8A'
                              }}>
                                {isFailed
                                  ? <AlertCircle size={15} style={{ flexShrink: 0 }} />
                                  : <Clock size={15} style={{ flexShrink: 0 }} />}
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {upload.titel}
                                </span>
                              </div>
                              <p style={{
                                fontSize: '12px',
                                color: isFailed ? '#B91C1C' : '#1D4ED8',
                                margin: '4px 0 0 0',
                                lineHeight: '1.4'
                              }}>
                                {isFailed
                                  ? `Versand fehlgeschlagen${upload.fehler_nachricht ? `: ${upload.fehler_nachricht}` : ''}`
                                  : `Versand geplant für ${formatBerlinDateTime(upload.geplanter_versand_am)} Uhr`}
                                {upload.erstellt_von_name && ` · ausgelöst von ${upload.erstellt_von_name}`}
                                {' · '}{upload.files.length} Datei{upload.files.length !== 1 ? 'en' : ''}
                              </p>
                            </div>
                            <button
                              onClick={() => {
                                if (isFailed) {
                                  setConfirmDeleteUploadId(confirmDeleteUploadId === upload.id ? null : upload.id);
                                } else {
                                  setConfirmCancelUploadId(confirmCancelUploadId === upload.id ? null : upload.id);
                                }
                              }}
                              disabled={busy}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '4px 10px',
                                backgroundColor: '#FFFFFF',
                                border: `1px solid ${isFailed ? '#FECACA' : '#BFDBFE'}`,
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: '500',
                                color: isFailed ? '#B91C1C' : '#1D4ED8',
                                cursor: busy ? 'not-allowed' : 'pointer',
                                flexShrink: 0
                              }}
                            >
                              {isFailed ? <Trash2 size={13} /> : <X size={13} />}
                              {isFailed ? 'Löschen' : 'Abbrechen'}
                            </button>
                          </div>

                          {/* Vollständige Sichtbarkeit: der komplette Kommentar —
                              exakt der Text, der in der Kundenmail stehen wird */}
                          {upload.kommentar && (
                            <div style={{
                              marginTop: '10px',
                              padding: '8px 10px',
                              backgroundColor: '#FFFFFF',
                              borderRadius: '4px',
                              borderLeft: `3px solid ${isFailed ? '#FECACA' : '#BFDBFE'}`
                            }}>
                              <p style={{ fontSize: '13px', color: '#374151', margin: 0, lineHeight: '1.4', whiteSpace: 'pre-line' }}>
                                {upload.kommentar}
                              </p>
                            </div>
                          )}

                          {/* Vollständige Sichtbarkeit: Liste der Dateianhänge inkl.
                              Download (die Dateien liegen beim Planen bereits im Storage;
                              nur Mail und Kunden-Sichtbarkeit warten auf den Zeitpunkt) */}
                          {upload.files.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '10px' }}>
                              {upload.files.map((file) => (
                                <div key={file.id} style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  padding: '8px 10px',
                                  backgroundColor: '#FFFFFF',
                                  borderRadius: '6px',
                                  border: `1px solid ${isFailed ? '#FECACA' : '#BFDBFE'}`
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                                    <span style={{ fontSize: '16px' }}>{getFileTypeIcon(file.original_filename)}</span>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <p style={{
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        color: '#374151',
                                        margin: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                      }}>
                                        {file.original_filename}
                                      </p>
                                      <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>
                                        {formatFileSize(file.file_size)}
                                      </p>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => handlePreviewDownload(file.id, file.storage_path, file.original_filename)}
                                    disabled={downloadingFiles.has(file.id)}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      padding: '4px 10px',
                                      backgroundColor: downloadingFiles.has(file.id) ? '#E5E7EB' : '#EFF6FF',
                                      color: downloadingFiles.has(file.id) ? '#9CA3AF' : '#2563EB',
                                      border: `1px solid ${downloadingFiles.has(file.id) ? '#D1D5DB' : '#BFDBFE'}`,
                                      borderRadius: '4px',
                                      cursor: downloadingFiles.has(file.id) ? 'not-allowed' : 'pointer',
                                      fontSize: '12px',
                                      fontWeight: '500',
                                      flexShrink: 0
                                    }}
                                  >
                                    <Download size={12} />
                                    {downloadingFiles.has(file.id) ? '...' : 'Download'}
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Bestätigungsdialog: Abbrechen (geplant) bzw. Löschen (fehler) */}
                          {confirmOpen && (
                            <div style={{
                              marginTop: '10px',
                              padding: '10px 12px',
                              backgroundColor: '#FFFFFF',
                              border: `1px solid ${isFailed ? '#FECACA' : '#BFDBFE'}`,
                              borderRadius: '6px'
                            }}>
                              <p style={{
                                margin: '0 0 10px 0',
                                fontSize: '13px',
                                color: isFailed ? '#991B1B' : '#1E3A8A',
                                fontWeight: '500',
                                lineHeight: '1.4'
                              }}>
                                {isFailed
                                  ? `Dieses fehlgeschlagene Paket mit ${upload.files.length} Datei${upload.files.length !== 1 ? 'en' : ''} unwiderruflich löschen?`
                                  : `Geplante Sendung abbrechen? Das Paket mit ${upload.files.length} Datei${upload.files.length !== 1 ? 'en' : ''} wird gelöscht und nicht versendet.`}
                              </p>
                              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                <button
                                  onClick={() => { setConfirmCancelUploadId(null); setConfirmDeleteUploadId(null); }}
                                  disabled={busy}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#F3F4F6',
                                    color: '#374151',
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    border: '1px solid #D1D5DB',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Zurück
                                </button>
                                <button
                                  onClick={() => isFailed ? handleDeleteUpload(upload.id) : handleCancelScheduled(upload.id)}
                                  disabled={busy}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: busy ? '#9CA3AF' : '#DC2626',
                                    color: 'white',
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: busy ? 'not-allowed' : 'pointer'
                                  }}
                                >
                                  {busy
                                    ? (isFailed ? 'Wird gelöscht...' : 'Wird abgebrochen...')
                                    : (isFailed ? 'Endgültig löschen' : 'Sendung abbrechen')}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {!selectedContactId ? (
              <div style={{ 
                padding: '32px 24px', 
                textAlign: 'center',
                backgroundColor: '#F9FAFB',
                borderRadius: '8px',
                border: '1px solid #E5E7EB'
              }}>
                <Eye size={32} color="#9CA3AF" style={{ marginBottom: '12px' }} />
                <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                  Bitte wählen Sie einen Ansprechpartner, um dessen Downloadbereich einzusehen.
                </p>
              </div>
            ) : previewLoading ? (
              <div style={{ 
                padding: '32px 24px', 
                textAlign: 'center',
                backgroundColor: '#F9FAFB',
                borderRadius: '8px',
                border: '1px solid #E5E7EB'
              }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  border: '3px solid #E5E7EB',
                  borderTop: '3px solid #3B82F6',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 12px'
                }} />
                <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                  Downloadbereich wird geladen...
                </p>
                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
              </div>
            ) : previewError ? (
              <div style={{ 
                padding: '12px', 
                backgroundColor: '#FEF2F2', 
                borderRadius: '8px',
                border: '1px solid #FECACA',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <AlertCircle size={16} color="#DC2626" />
                <span style={{ fontSize: '14px', color: '#DC2626' }}>{previewError}</span>
              </div>
            ) : previewUploads.length === 0 ? (
              <div style={{ 
                padding: '32px 24px', 
                textAlign: 'center',
                backgroundColor: '#F9FAFB',
                borderRadius: '8px',
                border: '1px solid #E5E7EB'
              }}>
                <Upload size={32} color="#9CA3AF" style={{ marginBottom: '12px' }} />
                <p style={{ fontSize: '14px', color: '#6B7280', margin: '0 0 4px 0', fontWeight: '500' }}>
                  Keine Dokumente vorhanden
                </p>
                <p style={{ fontSize: '13px', color: '#9CA3AF', margin: 0 }}>
                  Für diesen Ansprechpartner wurden noch keine Dokumente versendet.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Info-Banner */}
                <div style={{
                  padding: '10px 12px',
                  backgroundColor: '#F0F9FF',
                  borderRadius: '6px',
                  border: '1px solid #BAE6FD',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Eye size={14} color="#0369A1" />
                  <span style={{ fontSize: '13px', color: '#075985' }}>
                    So sieht der Kunde seinen Downloadbereich — {previewUploads.length} Upload{previewUploads.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {/* Upload-Karten */}
                {previewUploads.map((upload) => {
                  // Punkt 2 (Veralten-Tracking): veraltete Pakete gedämpft +
                  // eingeklappt; Hinweis „veraltete Fassung" an der Versendet-am-
                  // Zeile. Löschen bleibt jederzeit möglich.
                  const istVeraltet = !!upload.veraltet_am;
                  const istAufgeklappt = expandierteVeralteteVorschau.has(upload.id);
                  const zeigeInhalt = !istVeraltet || istAufgeklappt;
                  // Folgepaket: Nachfolger bzw. Vorgänger aus derselben Liste
                  // (identisch zur Kundenseite).
                  const nachfolger = istVeraltet && upload.ersetzt_durch_upload_id
                    ? previewUploads.find(u => u.id === upload.ersetzt_durch_upload_id)
                    : undefined;
                  const vorgaenger = upload.folgepaket_von_upload_id
                    ? previewUploads.find(u => u.id === upload.folgepaket_von_upload_id)
                    : undefined;
                  const aenderungen = upload.folgepaket_aenderungen ?? [];
                  // Interne Zusatzzeile (nur XTAX-Sicht, der Kunde sieht sie nicht):
                  // Uhrzeit und tatsächlich hochladende Person. Weicht der gewählte
                  // Absender (sender_email) davon ab, wird er mit angezeigt.
                  const zeitpunkt = upload.versendet_am || upload.created_at;
                  const hochgeladenVon = upload.erstellt_von_name || upload.erstellt_von_email;
                  const absenderEmail = (upload.sender_email || '').trim().toLowerCase();
                  const abweichenderAbsender = absenderEmail !== '' && absenderEmail !== (upload.erstellt_von_email || '').trim().toLowerCase()
                    ? (senders.find(s => (s.email || '').trim().toLowerCase() === absenderEmail)?.fullName || (upload.sender_email || '').trim())
                    : null;
                  const interneZeile = (
                    <p
                      title="Nur intern sichtbar. Der Kunde sieht diese Zeile nicht."
                      style={{ fontSize: '11px', color: '#9CA3AF', margin: '2px 0 0 0', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      <Lock size={11} style={{ flexShrink: 0 }} />
                      <span>
                        Hochgeladen von {hochgeladenVon}
                        {abweichenderAbsender && `, gesendet als ${abweichenderAbsender}`}
                        {` am ${formatGermanDate(zeitpunkt)} um ${formatGermanTime(zeitpunkt)} Uhr`}
                      </span>
                    </p>
                  );
                  return (
                  <div key={upload.id} style={{
                    padding: '16px',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '8px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                    opacity: istVeraltet ? 0.6 : 1
                  }}>
                    {/* Upload Header */}
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {istVeraltet ? (
                            <>
                              <p style={{ 
                                fontSize: '14px', 
                                fontWeight: '600', 
                                color: '#111827', 
                                margin: '0 0 4px 0' 
                              }}>
                                {upload.titel}
                              </p>
                              <p style={{ fontSize: '12px', color: '#6B7280', margin: 0 }}>
                                Versendet am {upload.versendet_am ? formatGermanDate(upload.versendet_am) : formatGermanDate(upload.created_at)}
                                {upload.erstellt_von_name && ` von ${upload.erstellt_von_name}`}
                                {' · veraltete Fassung'}
                                {nachfolger && ` · ersetzt durch das Paket vom ${formatGermanDate(nachfolger.versendet_am || nachfolger.created_at)}`}
                              </p>
                              {interneZeile}
                              <button
                                onClick={() => toggleVeralteteVorschau(upload.id)}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  marginTop: '6px',
                                  padding: '3px 8px',
                                  backgroundColor: 'transparent',
                                  color: '#6B7280',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  border: '1px solid #D1D5DB',
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}
                              >
                                {istAufgeklappt ? 'Ausblenden' : 'Dokumente anzeigen'}
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: istAufgeklappt ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                                  <polyline points="6 9 12 15 18 9"/>
                                </svg>
                              </button>
                            </>
                          ) : (
                            <>
                              <p style={{ 
                                fontSize: '14px', 
                                fontWeight: '600', 
                                color: '#111827', 
                                margin: '0 0 4px 0' 
                              }}>
                                {upload.titel}
                              </p>
                              <p style={{ fontSize: '12px', color: '#6B7280', margin: 0 }}>
                                Versendet am {upload.versendet_am ? formatGermanDate(upload.versendet_am) : formatGermanDate(upload.created_at)}
                                {upload.erstellt_von_name && ` von ${upload.erstellt_von_name}`}
                              </p>
                              {interneZeile}
                            </>
                          )}
                        </div>
                        <button
                          onClick={() => setConfirmDeleteUploadId(confirmDeleteUploadId === upload.id ? null : upload.id)}
                          disabled={deletingUploadIds.has(upload.id)}
                          title="Dokumentenpaket löschen"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '6px',
                            backgroundColor: 'transparent',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: deletingUploadIds.has(upload.id) ? 'not-allowed' : 'pointer',
                            flexShrink: 0,
                            marginLeft: '8px'
                          }}
                        >
                          <Trash2 size={16} color={confirmDeleteUploadId === upload.id ? '#DC2626' : '#9CA3AF'} />
                        </button>
                      </div>
                      {zeigeInhalt && upload.folgepaket_von_upload_id && (
                        <div style={{
                          marginTop: '8px',
                          padding: '8px 10px',
                          backgroundColor: '#EFF6FF',
                          border: '1px solid #BFDBFE',
                          borderRadius: '4px',
                          fontSize: '13px',
                          color: '#1E3A8A',
                          lineHeight: '1.4'
                        }}>
                          <div style={{ fontWeight: '600', marginBottom: '2px' }}>
                            Aktualisierte Fassung
                          </div>
                          <div>
                            {vorgaenger
                              ? `Dieses Paket ersetzt das Paket vom ${formatGermanDate(vorgaenger.versendet_am || vorgaenger.created_at)}.`
                              : 'Dieses Paket ersetzt ein zuvor bereitgestelltes Paket.'}
                            {' Bitte verwenden Sie nur noch diese Unterlagen.'}
                          </div>
                          {aenderungen.length > 0 && (
                            <>
                              <div style={{ marginTop: '6px' }}>Geändert haben sich:</div>
                              <ul style={{ margin: '2px 0 0 0', paddingLeft: '18px' }}>
                                {aenderungen.map((aenderung, index) => (
                                  <li key={index}>{beschreibeAenderung(aenderung)}</li>
                                ))}
                              </ul>
                            </>
                          )}
                        </div>
                      )}
                      {zeigeInhalt && upload.kommentar && (
                        <div style={{
                          marginTop: '8px',
                          padding: '8px 10px',
                          backgroundColor: '#F9FAFB',
                          borderRadius: '4px',
                          borderLeft: '3px solid #D1D5DB'
                        }}>
                          <p style={{ fontSize: '13px', color: '#374151', margin: 0, lineHeight: '1.4', whiteSpace: 'pre-line' }}>
                            {upload.kommentar}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Dateiliste — bei xtax_portal-Paketen nach Abschnitt gruppiert */}
                    {zeigeInhalt && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {renderPreviewFileList(upload)}
                    </div>
                    )}

                    {/* Bestätigungsdialog zum Löschen (immer verfügbar, auch bei
                        eingeklapptem veraltetem Paket) */}
                    {confirmDeleteUploadId === upload.id && (
                      <div style={{
                        marginTop: '12px',
                        padding: '12px',
                        backgroundColor: '#FEF2F2',
                        border: '1px solid #FECACA',
                        borderRadius: '6px'
                      }}>
                        <p style={{
                          margin: '0 0 10px 0',
                          fontSize: '13px',
                          color: '#991B1B',
                          fontWeight: '500',
                          lineHeight: '1.4'
                        }}>
                          Dieses Dokumentenpaket mit {upload.files.length} Datei{upload.files.length !== 1 ? 'en' : ''} unwiderruflich löschen?
                        </p>
                        <div style={{
                          display: 'flex',
                          gap: '8px',
                          justifyContent: 'flex-end'
                        }}>
                          <button
                            onClick={() => setConfirmDeleteUploadId(null)}
                            disabled={deletingUploadIds.has(upload.id)}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#F3F4F6',
                              color: '#374151',
                              fontSize: '13px',
                              fontWeight: '500',
                              border: '1px solid #D1D5DB',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            Abbrechen
                          </button>
                          <button
                            onClick={() => handleDeleteUpload(upload.id)}
                            disabled={deletingUploadIds.has(upload.id)}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: deletingUploadIds.has(upload.id) ? '#9CA3AF' : '#DC2626',
                              color: 'white',
                              fontSize: '13px',
                              fontWeight: '500',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: deletingUploadIds.has(upload.id) ? 'not-allowed' : 'pointer'
                            }}
                          >
                            {deletingUploadIds.has(upload.id) ? 'Wird gelöscht...' : 'Endgültig löschen'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}

            {/* NEU (Zugriffsprotokoll): Logins und Downloads des Kunden */}
            {selectedContactId && !previewLoading && (
              <div style={{ marginTop: '16px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginBottom: '8px'
                }}>
                  <History size={16} color="#374151" />
                  <p style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: 0 }}>
                    Zugriffsprotokoll
                  </p>
                </div>
                {accessLogLoading ? (
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#F9FAFB',
                    borderRadius: '8px',
                    border: '1px solid #E5E7EB'
                  }}>
                    <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>
                      Zugriffsprotokoll wird geladen...
                    </p>
                  </div>
                ) : accessLog.length === 0 ? (
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#F9FAFB',
                    borderRadius: '8px',
                    border: '1px solid #E5E7EB'
                  }}>
                    <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>
                      Noch keine Zugriffe protokolliert.
                    </p>
                  </div>
                ) : (
                  <div style={{
                    maxHeight: '240px',
                    overflowY: 'auto',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '8px',
                    border: '1px solid #E5E7EB'
                  }}>
                    {accessLog.map((entry) => (
                      <div key={entry.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '8px 12px',
                        borderBottom: '1px solid #F3F4F6'
                      }}>
                        <span style={{
                          fontSize: '12px',
                          color: '#6B7280',
                          whiteSpace: 'nowrap',
                          flexShrink: 0
                        }}>
                          {formatGermanDateTime(entry.created_at)}
                        </span>
                        {entry.event_type === 'login' ? (
                          <span style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>
                            Login
                          </span>
                        ) : (
                          <span style={{
                            fontSize: '13px',
                            color: '#374151',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            <span style={{ fontWeight: '500' }}>Download:</span> {entry.file_name || 'Unbekannte Datei'}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Schließen-Button für Downloadbereich */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button
                onClick={onClose}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Schließen
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SecureDocumentModal;
